// GroupChat.js  —  root orchestrator
//
// Hook call order (strictly top-down, zero forward references):
//   1. All useState + useRef (including notificationHistoryLoadedRef — local, like old code)
//   2. useFeedState      (pure state, no args)
//   3. useAppAuth        (no feed deps)
//   4. useSocketInit     (needs currentUser)
//   5. usePresenceAndUnread
//   6. useGroupChatState
//   7. P2P / BTC / USDT / Settings sockets
//   8. feedSocket (feedSocketRef kept in sync for safe self-reference)
//   9. feedActionSocket + userSocket
//  10. useEffects

import React, { useState, useEffect, useRef, useMemo, useCallback, } from "react";
import { useLocation, useNavigate }  from "react-router-dom";
import { toast, ToastContainer }     from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ── Layouts ── */
import DesktopSocialLayout from "../layouts/DesktopSocialLayout";
import DesktopChatLayout   from "../layouts/DesktopChatLayout";
import MobileAppLayout     from "../layouts/MobileAppLayout";
import MobileChatPanel     from "../layouts/MobileChatPanel";
import { AuthorPostsProvider } from "../context/AuthorPostsContext";
/* ── Pages / overlays ── */
import { LoginView }          from "./Login";
import { ForgotPasswordView } from "./ForgotPassword";
import { AccountView }        from "./Account";
import GroupOverlay           from "../components/GroupOverlay";
import Post                   from "./Post";
import AppInvite              from "./AppInvite";

/* ── Socket hooks ── */
import { useP2PSocket }      from "../hooks/useP2PSocket";
import { useUsdtSocket }     from "../hooks/useUsdtSocket";
import { useSettingsSocket } from "../hooks/useSettingsSocket";
import useFeedSocket         from "../hooks/useFeedSocket";
import useFeedActionSocket   from "../hooks/useFeedActionSocket";
import useUserSocket         from "../hooks/useUserSocket";
import useMediaSocket        from "../hooks/useMediaSocket";
import AvatarModal from "../components/AvatarModal";

/* ── Utilities ── */
import { uploadToR2 }            from "../components/Upload";
import { uuid }                  from "../utils/ChatUtils";
import { fetchPostWithComments } from "../components/postComment";
import { TABS, DEFAULT_TAB, MOBILE_PRIMARY_TABS } from "../constants/tabs";
import {
  fetchFeedHistory,
  fetchGlobalFeedHistory,
  fetchInteractionHistory,
    searchUsers,
} from "../components/feedHistory";

import {
  fetchMediaPosts,
  fetchGlobalMediaPosts,
    fetchMyMediaPosts,
    fetchUserMediaPosts,
} from "../components/mediaPost";
import {
  fetchMyPostHistory,
} from "../components/myPostHistory";
import { fetchNotificationHistory } from "../components/notificationHistory";
import MyPost                 from "./MyPost";
/* ── Local hooks (same folder) ── */
import { useAppAuth }           from "./useAppAuth";
import { useSocketInit }        from "./useSocketInit";
import { usePresenceAndUnread } from "./usePresenceAndUnread";
import { useFeedState }         from "./useFeedState";
import { useGroupChatState } from "./useGroupChatState";
import { useChatInstances } from "../hooks/useChatInstance";
import { sendSystemMessage } from "../utils/sendSystemMessage";
import { fetchChatHistory } from "../components/chatHistory";
import { fetchGroupChatHistory } from "../components/groupChatHistory";
import { fetchScheduledMessages } from "../components/schedule";
import { fetchGroupScheduledMessages } from "../components/groupSchedule";
import {
  fetchWalletBalance,
  fetchGhsxWalletBalance,
} from "../components/wallet";

import {
  fetchSellerStatus,
  fetchUSWDHistory,
  fetchUSWDRate,
} from "../components/uswd";

import {
  fetchGhsxHistory,
  fetchGhsxRate,
} from "../components/ghsx";
import useStatusSocket from "../hooks/useStatusSocket";
import {

  fetchUsdtAddress,
  fetchUsdtBalance,
  fetchUsdtHistory,
} from "../components/usdt";

import {

  fetchOffers,
   fetchOrderHistory,
   fetchSellerPaymentDetails,
 fetchMyPaymentProfile,
} from "../components/getp2p";
import useUswdSocket from "../hooks/useUswdSocket";
import useGhsxSocket from "../hooks/useGhsxSocket";
import BuyData, { useBuyData } from "./BuyData";
import {
  loadMessages,
  saveMessage,
  dbPromise,
  getAllScheduledMessages,
  saveScheduledMessage,
  loadStatuses,
  saveStatus,
} from "../db/fsDB";
import {

  getMyStatuses,
  getFollowingStatuses,

} from "../components/statusHistory";

export default function GroupChat() {

  /* ═══════════════════════════════════════
     STEP 1 — ALL useState + useRef
     NOTE: notificationHistoryLoadedRef is a LOCAL ref here,
     exactly like in the original monolithic GroupChat.
     It must NOT live in a custom hook because React StrictMode
     double-invokes hooks which can corrupt the guard.
  ═══════════════════════════════════════ */

  /* Chat */
  const [users,        setUsers]        = useState([]);
  const [groups,       setGroups]       = useState([]);
  const [messages,     setMessages]     = useState({});
  const [typingUsers,  setTypingUsers]  = useState([]);
   const [groupTyping,  setGroupTyping]  = useState({});
  const [groupMembers, setGroupMembers] = useState({});
  const [viewedPostsUser, setViewedPostsUser] = useState(null);
  const [viewedAuthorMediaPosts, setViewedAuthorMediaPosts] = useState([]);
  const authorMediaCacheRef = useRef(new Map());
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab,    setActiveTab]    = useState(() => {
  if (new URLSearchParams(window.location.search).get("tab") === "buy-data") {
    return TABS.BUY_DATA;
  }
  return localStorage.getItem("defaultTab") || DEFAULT_TAB;
});

  const [usersReady,  setUsersReady]  = useState(false);
  const [groupsReady, setGroupsReady] = useState(false);
 const [unreadFeedCount, setUnreadFeedCount] = useState(0);
// In STEP 1 useState block, add:
const [scheduledMessages, setScheduledMessages] = useState([]);
  const chatMetaReady = usersReady && groupsReady;

  /* Status */

const [
  statusNotifications,
  setStatusNotifications,
] = useState([]);

const [
  myStatusIds,
  setMyStatusIds,
] = useState([]);
const [hasUnreadStatus, setHasUnreadStatus] = useState(false);
const chatHydratedRef = useRef(false);

  /*
   * bootstrapDone — flips to true once the IDB bootstrap fetch+save
   * loop completes (or is skipped because IDB already had data).
   * The progressive hydration effect depends on this so it always
   * runs AFTER data is in IDB, even on a cold first load.
   */
  const [bootstrapDone, setBootstrapDone] = useState(false);
  const [feedBootstrapDone, setFeedBootstrapDone] =
  useState(false);

const feedHydratedRef = useRef(false);
const postPageLoadedRef = useRef(null); 


  /* UI */
  const [searchQuery,           setSearchQuery]           = useState("");
  const [menuOpen,              setMenuOpen]              = useState(false);
  const [createGroupMode,       setCreateGroupMode]       = useState(false);
  const [selectedUsersForGroup, setSelectedUsersForGroup] = useState([]);
  const [mobileView,            setMobileView]            = useState(false);
  const [avatarZoom,            setAvatarZoom]            = useState(null);
 const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored === "light" || stored === "dark") return stored;
    // No manual preference saved yet — fall back to the device setting
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  /* Chat input */
 // Draft state is now stored per chat instead of globally.
const [chatDrafts, setChatDrafts] = useState({});
  const [isUploading,    setIsUploading]    = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  /* P2P */
  const [p2pOffers,      setP2pOffers]      = useState([]);
  const [p2pOrders,      setP2POrders]      = useState([]);
  const [myAds,          setMyAds]          = useState([]);
  const [myUsdtAd, setMyUsdtAd] = useState(null);
  const [orderTimers,    setOrderTimers]    = useState({});
  const [activeP2POrder, setActiveP2POrder] = useState(null);
  const [activeP2PPage, setActiveP2PPage] = useState("market");
const [p2pEvent, setP2pEvent] = useState(null);
const [sellerPaymentDetails,
       setSellerPaymentDetails] =
useState([]);

const [sellerCurrency,
       setSellerCurrency] =
useState(null);

/* Cache payment details by sellerChatId */
const [sellerPaymentCache,
       setSellerPaymentCache] =
useState({});

  /* Asset / wallet */
  const [assetView,   setAssetView]   = useState("list");
  const [usdtAddress, setUsdtAddress] = useState(null);
  const [usdtBalance, setUsdtBalance] = useState(null);
  const [usdtHistory,
       setUsdtHistory] =
  useState([]);
  const [uswdHistory, setUswdHistory] = useState([]);
  const [ghsxHistory, setGhsxHistory] = useState([]);
  const [sendAsset,   setSendAsset]   = useState(null);
  const [wallet,      setWallet]      = useState(null);
  const [uswdWallet,       setUswdWallet]       = useState(null);
  // Cached purchase pricing options
const [uswdPricing, setUswdPricing] = useState(null);
const [ghsxPricing, setGhsxPricing] = useState(null);
  useEffect(() => {

}, [uswdWallet]);
const [uswdWalletLoading,setUswdWalletLoading]= useState(true);
const [uswdWalletError,  setUswdWalletError]  = useState(null);
const [isApprovedSeller, setIsApprovedSeller] = useState(false);

const [, setGhsxWalletLoading] =
  useState(false);

const [, setGhsxWalletError] =
  useState(null);
const [ghsxWallet, setGhsxWallet] = useState(null);
const [rentRefundRequest, setRentRefundRequest] =
  useState(null);
  /* Social */
  const [commentsByPost, setCommentsByPost] = useState({});
  const [nearbyUsers,    setNearbyUsers]    = useState([]);
  const [searchResults, setSearchResults] = useState([]);
const [searchLoading, setSearchLoading] = useState(false);

/* =========================================================
   🎬 MEDIA STATE (Independent of Feed)
========================================================= */

const [globalMediaLivePosts, setGlobalMediaLivePosts] = useState([]);
const [followingMediaPosts, setFollowingMediaPosts] = useState([]);
const [commentedMediaPosts, setCommentedMediaPosts] = useState([]);
const [myMediaPosts, setMyMediaPosts] = useState([]);
const [mediaCommentsByPost, setMediaCommentsByPost] = useState({});
const [mediaNotifications, setMediaNotifications] = useState([]);
  // comment pagination
const [commentSkip,    setCommentSkip]    = useState({});
const [commentHasMore, setCommentHasMore] = useState({});
const [commentLoading, setCommentLoading] = useState({});

  /* Refs — all declared together at the top */
  const typingTimeoutRef = useRef(null);
  const menuRef          = useRef(null);
  const pendingPreviewsRef = useRef([]);
  const usdtSendSuccessRef = useRef(null);
const usdtSendErrorRef = useRef(null);

  /* 🔥 Decrypt Worker */
  const decryptWorkerRef = useRef(null);
  const statusDecryptWorkerRef =
useRef(null);

const statusHydratedRef =
useRef(false);

  useEffect(() => {
    decryptWorkerRef.current = new Worker(
      new URL("../workers/decryptWorker.js", import.meta.url)
    );
    return () => {
      decryptWorkerRef.current?.terminate();
    };
  }, []);

  useEffect(() => {

  statusDecryptWorkerRef.current =
    new Worker(

      new URL(

        "../workers/statusDecryptWorker.js",

        import.meta.url

      )

    );

  return () => {

    statusDecryptWorkerRef.current?.terminate();

  };

}, []);

/* ─────────────────────────────────────────────
   STATUS CLEANUP LISTENER
───────────────────────────────────────────── */

useEffect(() => {

  const onExpired = (e) => {

    const statusIds =
      e.detail?.statusIds || [];

    if (!statusIds.length) {
      return;
    }

    setStatusNotifications((prev) =>
      prev.filter(
        (status) =>
          !statusIds.includes(
            status.statusId
          )
      )
    );

    setMyStatusIds((prev) =>
      prev.filter(
        (id) =>
          !statusIds.includes(id)
      )
    );

  };

  window.addEventListener(
    "status:expired",
    onExpired
  );

  return () => {

    window.removeEventListener(
      "status:expired",
      onExpired
    );

  };

}, []);

  /*
   * notificationHistoryLoadedRef — declared HERE in GroupChat (not in a hook)
   * so it is a stable singleton for the lifetime of this component instance,
   * matching the original monolithic behaviour exactly.
   */
  const notificationHistoryLoadedRef = useRef(false);

  /*
   * feedSocketRef — always points to the live feedSocket instance so that
   * notification callbacks can call clearAllNotifications() without holding
   * a stale closure over the const assigned below.
   */
  const feedSocketRef = useRef(null);

  /* Stable key helpers */
 /* Stable key helpers */
const getUserKey = (chatId) => (chatId ? `user-${chatId}` : null);
const getGroupKey = (groupId) => (groupId ? `group-${groupId}` : null);

/* Active chat key */
const activeChatKey = React.useMemo(() => {
  if (selectedItem?.type === "group") {
    return getGroupKey(selectedItem.groupId);
  }

  if (selectedItem?.type === "user" && selectedItem?.chatId) {
    return getUserKey(selectedItem.chatId);
  }

  return null;
}, [
  selectedItem?.type,
  selectedItem?.chatId,
  selectedItem?.groupId,
  getGroupKey,
  getUserKey,
]);

// Real selection always wins. Only synthesize a display item when
// nothing has been explicitly selected yet, so ChatHeader / socket
// effects have something to work with during the auto-hydrated
// fallback — without this becoming the "selected" chat anywhere else.
const fallbackItem = React.useMemo(() => {
  if (selectedItem || !activeChatKey) return null;

  if (activeChatKey.startsWith("group-")) {
    const groupId = activeChatKey.slice("group-".length);
    const g = groups.find((g) => String(g.groupId) === groupId);

    return g
      ? {
          type: "group",
          groupId: g.groupId,
          name: g.name,
          groupImageUrl: g.groupImageUrl,
        }
      : null;
  }

  const chatId = activeChatKey.slice("user-".length);
  const u = users.find((u) => String(u.chatId) === chatId);

  return u
    ? {
        type: "user",
        chatId: u.chatId,
        displayName: u.displayName,
        username: u.username,
        profileImageUrl: u.profileImageUrl,
        online: u.online,
        lastSeenAt: u.lastSeenAt,
      }
    : null;
}, [selectedItem, activeChatKey, users, groups]);

  /* ═══════════════════════════════════════
     STEP 2 — FEED STATE (pure, no args)
  ═══════════════════════════════════════ */
  const {

  /* Following */
  feedPosts,
  setFeedPosts,

  feedHasMore,
  setFeedHasMore,

  feedLoading,
  setFeedLoading,

  /* Global */
  globalLivePosts,
  setGlobalLivePosts,

  globalHasMore,
  setGlobalHasMore,

  globalLoading,
  setGlobalLoading,

  /* Interactions */
  interactionPosts,
  setInteractionPosts,

  interactionHasMore,
  setInteractionHasMore,

  interactionLoading,
  setInteractionLoading,

  /* Notifications */
  notificationCounts,
  setNotificationCounts,

  /* Interaction meta */
  interactionActivity,
  setInteractionActivity,

  interactionCounts,
  setInteractionCounts,

  /* My Posts */
  myPostsHasMore,
  setMyPostsHasMore,

  myPostsLoading,
  setMyPostsLoading,

} = useFeedState();

  /* ═══════════════════════════════════════
     STEP 3 — AUTH
  ═══════════════════════════════════════ */
  const {
    authChecked,
    currentUser,
    setCurrentUser,
    authScreen,
    setAuthScreen,
    handleLoginSuccess,
     handleLogout: _handleLogout,
    sessionExpiredHandlerRef,
     justLoggedInRef,     
  } = useAppAuth();

  const realMyChatId = currentUser?.chatInfo?.[0]?.chatId || null;
   const buyDataStore = useBuyData(Boolean(currentUser?._id));
/* =========================================================
   🎬 MEDIA POST API BOOTSTRAP
   ---------------------------------------------------------
   Socket.IO handles NEW/live media posts.

   REST API handles EXISTING media posts when:
   - the app starts
   - the user refreshes the page
   - the socket reconnects after the initial load

   Bootstrap loads:
   - Following media posts
   - Global / For-You media posts
   - My media posts
   - Author media posts for authors appearing in the feed

   Author media is PREFETCHED here so tapping an author's
   avatar does not need to wait for another API request.

   Endpoints:
   GET /api/media-post
   GET /api/media-post/global
   GET /api/mymediaposts
   GET /api/media-post/user/:chatId
========================================================= */
useEffect(() => {
  if (!realMyChatId) return;

  let cancelled = false;

  const loadMediaPosts = async () => {
    try {
      /* -------------------------------------------------
         STEP 1
         Load the normal media feeds + my media together
      ------------------------------------------------- */
      const [
        followingResult,
        globalResult,
        myMediaResult,
      ] = await Promise.all([
        fetchMediaPosts({ limit: 100 }),
        fetchGlobalMediaPosts({ limit: 100 }),
        fetchMyMediaPosts({ limit: 100 }),
      ]);

      if (cancelled) return;

      const followingPosts =
        Array.isArray(followingResult?.posts)
          ? followingResult.posts
          : [];

      const globalPosts =
        Array.isArray(globalResult?.posts)
          ? globalResult.posts
          : [];

      const myPosts =
        Array.isArray(myMediaResult?.posts)
          ? myMediaResult.posts
          : [];

      /* ---------------------------------------------
         LOAD FOLLOWING MEDIA POSTS
      --------------------------------------------- */
      setFollowingMediaPosts(followingPosts);

      /* ---------------------------------------------
         LOAD GLOBAL / FOR-YOU MEDIA POSTS
      --------------------------------------------- */
      setGlobalMediaLivePosts(globalPosts);

      /* ---------------------------------------------
         LOAD MY MEDIA POSTS
         GET /api/mymediaposts
      --------------------------------------------- */
      setMyMediaPosts(myPosts);

      /* ---------------------------------------------
         COMBINE ALL MEDIA POSTS WITHOUT DUPLICATES
      --------------------------------------------- */
      const allMediaPosts = Array.from(
        new Map(
          [
            ...followingPosts,
            ...globalPosts,
            ...myPosts,
          ]
            .filter((post) => post?.postId)
            .map((post) => [
              String(post.postId),
              post,
            ])
        ).values()
      );

      /* ---------------------------------------------
         COMMENTS RETURNED BY THE MEDIA API
      --------------------------------------------- */
      const commentsMap = {};

      allMediaPosts.forEach((post) => {
        if (
          post?.postId &&
          Array.isArray(post.comments)
        ) {
          commentsMap[post.postId] =
            post.comments;
        }
      });

      setMediaCommentsByPost((prev) => ({
        ...prev,
        ...commentsMap,
      }));

      /* =================================================
         STEP 2
         FIND UNIQUE AUTHORS FROM THE LOADED MEDIA FEED

         We only prefetch authors that actually appear
         in the currently loaded media posts.

         The current user's own posts are already loaded
         through fetchMyMediaPosts(), so they are excluded.
      ================================================= */
      const authorChatIds = Array.from(
        new Set(
          allMediaPosts
            .map((post) => post?.author?.chatId)
            .filter(Boolean)
            .map(String)
            .filter(
              (chatId) =>
                chatId !== String(realMyChatId)
            )
        )
      );

      /* =================================================
         STEP 3
         PREFETCH EACH AUTHOR'S MEDIA POSTS

         GET /api/media-post/user/:chatId
      ================================================= */
      const authorResults = await Promise.all(
        authorChatIds.map(async (chatId) => {
          try {
            const result =
              await fetchUserMediaPosts({
                chatId,
                limit: 100,
              });

            return {
              chatId,
              result,
            };
          } catch (error) {

            return {
              chatId,
              result: null,
            };
          }
        })
      );

      if (cancelled) return;

      /* =================================================
         STEP 4
         SAVE AUTHOR MEDIA INTO CACHE

         This means avatar taps can read the data locally
         instead of making another API request.
      ================================================= */
      const authorCommentsMap = {};

      authorResults.forEach(
        ({ chatId, result }) => {
          const posts =
            Array.isArray(result?.posts)
              ? result.posts
              : [];

          authorMediaCacheRef.current.set(
            String(chatId),
            {
              posts,
              nextCursor:
                result?.nextCursor ?? null,
              hasMore:
                Boolean(result?.hasMore),
              loaded: true,
            }
          );

          /* -------------------------------------------
             Also cache comments returned by the
             author-specific media endpoint.
          ------------------------------------------- */
          posts.forEach((post) => {
            if (
              post?.postId &&
              Array.isArray(post.comments)
            ) {
              authorCommentsMap[post.postId] =
                post.comments;
            }
          });
        }
      );

      /* ---------------------------------------------
         MERGE AUTHOR COMMENTS INTO COMMENT CACHE
      --------------------------------------------- */
      if (
        Object.keys(authorCommentsMap).length > 0
      ) {
        setMediaCommentsByPost((prev) => ({
          ...prev,
          ...authorCommentsMap,
        }));
      }

    } catch (error) {
      if (cancelled) return;
    }
  };

  loadMediaPosts();

  return () => {
    cancelled = true;
  };
}, [realMyChatId]);

  const myUserName   =
    currentUser?.chatInfo?.find(
      (ci) => String(ci.chatId) === String(realMyChatId)
    )?.userName || null;

    const myDisplayName =
  currentUser?.chatInfo?.find(
    (ci) => String(ci.chatId) === String(realMyChatId)
  )?.displayName || null;

const activeDraft = activeChatKey
  ? chatDrafts[activeChatKey] || {}
  : {};

const updateChatDraft = React.useCallback((chatKey, patch) => {
  if (!chatKey) return;

  setChatDrafts((prev) => ({
    ...prev,
    [chatKey]: {
      ...(prev[chatKey] || {}),
      ...patch,
    },
  }));
}, []);

const input = activeDraft.input || "";
const setInput = (value) =>
  updateChatDraft(activeChatKey, { input: value });

const selectedReply = activeDraft.reply || null;
const setSelectedReply = (reply) =>
  updateChatDraft(activeChatKey, { reply });

const pendingMedia = activeDraft.media || null;
const setPendingMedia = (media) =>
  updateChatDraft(activeChatKey, { media });

const { chatInstances } = useChatInstances({
  activeChatKey,
  selectedItem,
  chatMetaReady,
  currentUser,
  users,
  groups,
});
    
/* ─────────────────────────
   FETCH USDT WALLET
───────────────────────── */

useEffect(() => {

  if (!currentUser) return;

  let cancelled = false;

  (async () => {

    try {

     const [

  addressData,
  balanceData,
  historyData,

] = await Promise.all([

  fetchUsdtAddress(),
  fetchUsdtBalance(),
  fetchUsdtHistory(),

]);

      if (cancelled) return;

      setUsdtAddress(
        addressData?.address || null
      );

     setUsdtBalance({

   balance:
   balanceData?.balance || 0,

   committed:
   balanceData?.committed || 0,

   available:
   balanceData?.available || 0,

});

setUsdtHistory(

  historyData?.history ||

  []

);

    } catch (err) {

    }

  })();

  return () => {

     cancelled = true;

  };

}, [currentUser?._id]);

useEffect(() => {

  if (!currentUser) return;

  let cancelled = false;

  (async () => {

    try {

      const data =
      await fetchOrderHistory();

      if (cancelled) return;

      setP2POrders(
        data?.orders || []
      );

    } catch (err) {

    }

  })();

  return () => {

    cancelled = true;

  };

}, [currentUser?._id]);


/* ─────────────────────────────────────────────
   LOAD MY P2P PAYMENT PROFILE
───────────────────────────────────────────── */

useEffect(() => {

  if (!currentUser) return;

  let cancelled = false;

  (async () => {

    try {

      const data =
        await fetchMyPaymentProfile();

      if (cancelled) return;

      setSellerCurrency(
        data?.currency ?? null
      );

      setSellerPaymentDetails(
        data?.paymentDetails ?? []
      );

    } catch (err) {

      if (!cancelled) {

        setSellerCurrency(null);

        setSellerPaymentDetails([]);

      }

    }

  })();

  return () => {

    cancelled = true;

  };

}, [currentUser?._id]);

useEffect(() => {

  if (!currentUser) return;  // ← ADD: wait for auth

  let cancelled = false;

  (async () => {

    try {

      const data =
      await fetchOffers(
        "USWD"
      );

      if (cancelled) return;

      setP2pOffers(

        data?.offers ||

        data?.ads ||

        []

      );

    } catch (err) {


    }

  })();

  return () => {

    cancelled = true;

  };

}, [currentUser?._id]);   // ← CHANGE: re-runs on every login

const refreshUswdBalance = async () => {

  try {

    setUswdWalletLoading(true);

    const [uswdData, ghsxData] = await Promise.all([
      fetchWalletBalance(),
      fetchGhsxWalletBalance(),
    ]);

    setUswdWallet(uswdData?.wallet ?? null);
    setGhsxWallet(ghsxData?.wallet ?? null);

  } catch {

    /* silently ignore — user can retry */

  } finally {

    setUswdWalletLoading(false);

  }

};

/* =========================================
   REFRESH GHSX BALANCE + HISTORY
========================================= */

const refreshGhsxBalance = async () => {
  try {
    setGhsxWalletLoading(true);

    const [walletData, historyData] =
      await Promise.all([
        fetchGhsxWalletBalance(),
        fetchGhsxHistory(),
      ]);

    setGhsxWallet(
      walletData?.wallet ?? null
    );

    setGhsxHistory(
      historyData?.history ?? []
    );
  } catch {
    /* silently ignore — user can retry */
  } finally {
    setGhsxWalletLoading(false);
  }
};

const refreshUsdtBalance = async () => {

  try {

    const [

      balanceData,
      historyData,

    ] = await Promise.all([

      fetchUsdtBalance(),
      fetchUsdtHistory(),

    ]);

    setUsdtBalance({

      balance:
        balanceData?.balance || 0,

      committed:
        balanceData?.committed || 0,

      available:
        balanceData?.available || 0,

    });

    setUsdtHistory(

      historyData?.history ||

      []

    );

  } catch (err) {

  }

};

const refreshAfterUswdPurchase = async () => {
  await Promise.all([
    refreshGhsxBalance(),
    refreshUswdBalance(),
    refreshUsdtBalance(),
  ]);
};

const refreshOffers =
useCallback(
  async (
    asset = "USWD",
    currency = null,
    page = 1
  ) => {

    try {

      const data =
        await fetchOffers(
          asset,
          currency,
          page,
          50
        );

      return data;

    } catch (err) {

      return {
        offers: [],
        hasMore: false,
      };
    }
  },
  []
);

const loadSellerPaymentDetails =
useCallback(
async (
   sellerChatId,
   selectedType = null
) => {

   // Clear the previous seller's payment details/currency immediately.
   // Without this, the stale array from whichever seller was last
   // viewed stays in state while this fetch (or cache lookup) is in
   // flight. That causes the "auto-select first payment type" effect
   // in P2P.js to briefly pick the OLD seller's payment type. When
   // the real data for the NEW seller then arrives, that stale type
   // no longer matches anything in the new list, so the details block
   // vanishes — the "appear then disappear" flicker you're seeing.
   // On a second click the seller is already cached, so this whole
   // race window closes instantly, which is why it "works" then.
   setSellerPaymentDetails([]);
   setSellerCurrency(null);

   let data;

   // ✅ Use cache if already loaded
   if (
      sellerPaymentCache[
         sellerChatId
      ]
   ) {

      data =
      sellerPaymentCache[
         sellerChatId
      ];

   } else {

      data =
      await fetchSellerPaymentDetails(
         sellerChatId
      );

      setSellerPaymentCache(
         prev => ({

            ...prev,

            [sellerChatId]:
               data,

         })
      );

   }

   setSellerCurrency(
      data?.currency || null
   );

   setSellerPaymentDetails(
      data?.paymentDetails || []
   );

   return data;

},
[
   sellerPaymentCache,
]);

  /* Logout — passes all live setters at call time, no forward refs */
 const handleLogout = () =>
    _handleLogout({
      setUsers, setGroups,
      setMessages,
      setFeedPosts, setGlobalLivePosts, setInteractionPosts,
      setCommentsByPost, setNotificationCounts,
      setInteractionCounts, setInteractionActivity,
       setUswdWallet,
        setP2pOffers,        // ← ADD THIS
    setP2POrders,        // ← ADD THIS
    setIsApprovedSeller,
    setUswdWalletLoading,
    setUswdWalletError,
    setSellerCurrency,
setSellerPaymentDetails,
setSellerPaymentCache,
      notificationHistoryLoadedRef,
      idbHydratedRef,
      hydrationSentRef,
          statusHydratedRef,
      setBootstrapDone,
      setUsersReady,
      setGroupsReady,
      feedHydratedRef,          // reset so feed re-bootstraps after login
      setFeedBootstrapDone,     // reset so post page guard re-arms
       });
  /* Token expired -> run the SAME full logout (clears IDB messages + all state) */
  sessionExpiredHandlerRef.current = handleLogout;

  /* ═══════════════════════════════════════
     STEP 4 — SOCKET + E2EE
  ═══════════════════════════════════════ */
const { socket, setSocket, cryptoFns, plaintextPreviewRef, getStatusKey, } = useSocketInit({
    currentUser,
    users,
      messages,  
    setUsers, setGroups, setMessages,
    setTypingUsers, setGroupTyping, setGroupMembers,
    setP2pOffers, setP2POrders, setMyAds,
    setSelectedItem, getUserKey, getGroupKey, setOrderTimers,
    selectedItem,
    // Mobile keeps a chat "selected" while you're on Feed/Assets, so selection
    // alone doesn't mean it's on screen.
    isChatsVisible: !mobileView || activeTab === TABS.CHATS,
    onUsersReady: () => {
      setUsersReady(true);
    },
    onGroupsReady: () => {
      setGroupsReady(true);
    },
  });

  // ADD after line 1271 (after `});` of useSocketInit)

  // Going back to the chat list: tell the server no chat is open any more,
  // otherwise it keeps treating this user as "viewing" and never counts unread.
  const hadOpenChatRef = useRef(false);
  useEffect(() => {
    if (selectedItem) {
      hadOpenChatRef.current = true;
      return;
    }
    if (!socket || !hadOpenChatRef.current) return;
    hadOpenChatRef.current = false;
    socket.emit("leave-chat");
  }, [selectedItem, socket]);

  /* ── Fetch USWD + GHSX balances + seller status ONLY when needed ── */
// AFTER
useEffect(() => {

  if (!currentUser) return;

  // Already hydrated — don't refetch on every render.

  if (uswdWallet?.uswdAddress && ghsxWallet?.ghsxAddress) {

    setUswdWalletLoading(false);

    return;

  }

  let cancelled = false;

  (async () => {

    try {

      setUswdWalletLoading(true);

      setUswdWalletError(null);

const [
  uswdBalanceData,
  ghsxBalanceData,
  statusData,
  uswdHistoryData,
  ghsxHistoryData,
] = await Promise.all([
  fetchWalletBalance(),
  fetchGhsxWalletBalance(),
  fetchSellerStatus(),
  fetchUSWDHistory(),
  fetchGhsxHistory(),
]);
      if (!cancelled) {

        setUswdWallet(uswdBalanceData?.wallet ?? null);

        setGhsxWallet(ghsxBalanceData?.wallet ?? null);

        setIsApprovedSeller(
          Boolean(statusData?.sellerApproved)
        );

     setUswdHistory(uswdHistoryData?.history || []);
setGhsxHistory(ghsxHistoryData?.history || []);

      }

    } catch (err) {

      if (!cancelled) {

        setUswdWalletError(
          err?.error || "Failed to load wallet balances"
        );

      }

    } finally {

      if (!cancelled) {

        setUswdWalletLoading(false);

      }

    }

  })();

  return () => {

    cancelled = true;

  };
}, [

  currentUser?._id,

  uswdWallet?.uswdAddress,

]);

/* ─────────────────────────────────────────────
   PREFETCH USWD + GHSX PRICING OPTIONS
   Fetch pricing independently of wallet balances.
───────────────────────────────────────────── */
useEffect(() => {
  if (!currentUser?._id) {
    setUswdPricing(null);
    setGhsxPricing(null);
    return;
  }

  let cancelled = false;

  const prefetchPricing = async () => {
    const [uswdResult, ghsxResult] = await Promise.allSettled([
      fetchUSWDRate(),
      fetchGhsxRate(),
    ]);

    if (cancelled) return;

    setUswdPricing(
      uswdResult.status === "fulfilled"
        ? uswdResult.value?.data ?? null
        : null
    );

    setGhsxPricing(
      ghsxResult.status === "fulfilled"
        ? ghsxResult.value?.data ?? null
        : null
    );
  };

  prefetchPricing();

  return () => {
    cancelled = true;
  };
}, [currentUser?._id]);

  /* ═══════════════════════════════════════
     STEP 5 — PRESENCE + UNREAD
  ═══════════════════════════════════════ */
  const { previewDecryptDoneRef } = usePresenceAndUnread({
    socket,
    myChatId: realMyChatId,
    selectedItem,
    users,
    setUsers,
    setTypingUsers,
    setGroupTyping,
    setGroups,
    plaintextPreviewRef,
  });

  /* ═══════════════════════════════════════
     STEP 6 — GROUP CHAT STATE
  ═══════════════════════════════════════ */
  const { handleInviteSuccess, handleInviteFailure, openDmChat } =
    useGroupChatState({
      currentUser, socket, users, groups,
      myChatId: realMyChatId,
      setGroups, setSelectedItem, setActiveTab,
      setCommentsByPost, setGlobalLivePosts, setFeedPosts, setInteractionPosts,  p2pOrders,
    setActiveP2POrder,
    });
    

  /* ═══════════════════════════════════════
     STEP 7 — P2P / BTC / USDT / SETTINGS
  ═══════════════════════════════════════ */
  const showToast = (
  msg,
  type = "info"
) => toast[type](msg);

const {
  handleConfirmPayment,
} = useP2PSocket({
  socket,
  userId: currentUser?._id || null,
  myChatId: realMyChatId,

  setP2pOffers,
  setP2POrders,
  setMyUsdtAd,
  setMyAds,
  setOrderTimers,
  setWallet: setUswdWallet,

  setSellerCurrency,
  setSellerPaymentDetails,

  showToast,

  onAdCreated: (payload) =>
    setP2pEvent({
      type: "AD_CREATED",
      payload,
    }),

  onOrderCreated: (payload) =>
    setP2pEvent({
      type: "ORDER_CREATED",
      payload,
    }),

  // ✅ NEW: fired after p2p:update-ad:success / :error so P2P.js
  // can close the edit form and show the refreshed ad.
  onAdUpdated: (payload) =>
    setP2pEvent({
      type: "AD_UPDATED",
      payload,
    }),

  onUpdateAdError: (payload) =>
    setP2pEvent({
      type: "UPDATE_AD_ERROR",
      payload,
    }),

  // ✅ NEW: fired after uswd:get-my-ad:success / :error — lets
  // P2P.js know the seller's current active USWD ad on load.
  onMyAdLoaded: (ad) =>
    setP2pEvent({
      type: "MY_AD_LOADED",
      payload: { ad },
    }),

  onGetMyAdError: (payload) =>
    setP2pEvent({
      type: "GET_MY_AD_ERROR",
      payload,
    }),
  onSellerRate: (payload) =>
    setP2pEvent({
      type: "SELLER_RATE",
      payload,
    }),

  onSellerRateError: (payload) =>
    setP2pEvent({
      type: "SELLER_RATE_ERROR",
      payload,
    }),

      onPaymentDetailsSaved: (payload) =>
    setP2pEvent({
      type: "PAYMENT_DETAILS_SAVED",
      payload,
    }),
 
   onOrderCompleted: (payload) => {

  const order = payload;

  // ONLY SELLER SENDS
  if (
    String(realMyChatId) !==
    String(order.sellerChatId)
  ) {

    setP2pEvent({
      type: "ORDER_COMPLETED",
      payload,
    });

    return;
  }

  (async () => {

    await sendSystemMessage({
      socket,
      myChatId: order.sellerChatId,
      recipientChatId: order.buyerChatId,

      senderText:
        `${order.asset} order ${order.orderId} has been completed.\n\nTap the Assets tab below to check your balance.`,

      recipientText:
        `${order.asset} order ${order.orderId} has been completed.\n\nTap the Assets tab below to check your balance.`,

      system: true,

      getUserKey,
      setMessages,
      setUsers,
      showToast,
      uuid,

      file: null,
      uploadMedia: null,
      setIsUploading: null,
      setUploadProgress: null,
    });

  })();

  setP2pEvent({
    type: "ORDER_COMPLETED",
    payload,
  });
},

onOrderExpired: (payload) => {

  const order = payload;

  // ONLY SELLER SENDS
  if (
    String(realMyChatId) !==
    String(order.sellerChatId)
  ) {

    setP2pEvent({
      type: "ORDER_EXPIRED",
      payload,
    });

    return;
  }

  (async () => {

    await sendSystemMessage({
      socket,
      myChatId: order.sellerChatId,
      recipientChatId: order.buyerChatId,

      senderText:
        `${order.asset} order ${order.orderId} has expired.`,

      recipientText:
        `${order.asset} order ${order.orderId} has expired.`,

      system: true,

      getUserKey,
      setMessages,
      setUsers,
      showToast,
      uuid,

      file: null,
      uploadMedia: null,
      setIsUploading: null,
      setUploadProgress: null,
    });

  })();

  setP2pEvent({
    type: "ORDER_EXPIRED",
    payload,
  });
},
 
  // ✅ NEW: First-time USWD seller flow — fired after
  // p2p:update-currency:success / :error so P2P.js can close the
  // currency picker and return to the sell form.
  onUpdateCurrencySuccess: (payload) =>
    setP2pEvent({
      type: "UPDATE_CURRENCY_SUCCESS",
      payload,
    }),
 
  onUpdateCurrencyError: (payload) =>
    setP2pEvent({
      type: "UPDATE_CURRENCY_ERROR",
      payload,
    }),
 
});
 

const usdt = useUsdtSocket({
  socket,
  chatId: realMyChatId,

  setUsdtAddress,
  setUsdtBalance,
    setUsdtHistory,
 onSendSuccess: usdtSendSuccessRef,
    onSendError: usdtSendErrorRef,
  showToast,

  onAdCreated: (payload) =>
    setP2pEvent({
      type: "USDT_AD_CREATED",
      payload,
    }),
onAdUpdated: (payload) => {
  if (payload?.ad) {
    setMyUsdtAd(payload.ad);
  }

  setP2pEvent({
    type: "USDT_AD_UPDATED",
    payload,
  });
},

onAdUpdateError: (error) =>
  setP2pEvent({
    type: "USDT_AD_UPDATE_ERROR",
    payload: { error },
  }),

   onMyAdFetched: (payload) => {
  setMyUsdtAd(payload?.ad ?? null);

  setP2pEvent({
    type: "USDT_MY_AD_FETCHED",
    payload,
  });
},

onMyAdFetchError: (error) =>
  setP2pEvent({
    type: "USDT_MY_AD_ERROR",
    payload: { error },
  }),

  onAdError: (error) =>
    setP2pEvent({
      type: "USDT_AD_ERROR",
      payload: { error },
    }),

onAdCancelled: (payload) => {
  setMyUsdtAd(null);

  setP2pEvent({
    type: "USDT_AD_CANCELLED",
    payload,
  });
},
  onAdCancelError: (error) =>
    setP2pEvent({
      type: "USDT_AD_CANCEL_ERROR",
      payload: { error },
    }),

  onAdDeleted: (payload) =>
    setP2pEvent({
      type: "AD_DELETED",
      payload,
    }),

  onDeleteAdError: (payload) =>
    setP2pEvent({
      type: "DELETE_AD_ERROR",
      payload,
    }),

  onOrderCreated: (payload) =>
    setP2pEvent({
      type: "USDT_ORDER_CREATED",
      payload,
    }),

  onOrderError: (error) =>
    setP2pEvent({
      type: "USDT_ORDER_ERROR",
      payload: { error },
    }),
});

const onRentRefundRequired = useCallback((payload) => {
  setRentRefundRequest(payload);
}, []);

  const settingsSocket = useSettingsSocket({
    socket, myChatId: realMyChatId,
    setCurrentUser, setUsers, setSelectedItem, handleLogout,  onRentRefundRequired,
  });

  const uswdSocket = useUswdSocket({

  socket,

  chatId: realMyChatId,

  setUswdWallet,

  setUswdHistory,

  onTransferResult: (payload) => {

    if (!payload?.success) {

      toast.error(
        payload?.error ||
        "USWD transfer failed"
      );

    }

  },

  onBalanceUpdated: (payload) => {

    if (payload?.amount) {

      toast.success(
        `Received ${payload.amount} USWD`
      );

    }

  },

});

const ghsxSocket = useGhsxSocket(socket, {
  onTransferSuccess: async () => {
    await refreshGhsxBalance();
    showToast("GHSX sent successfully.");
  },

  onReceived: async () => {
    await refreshGhsxBalance();
    showToast("GHSX received.");
  },

  onTransferError: ({ error }) => {
    showToast(error || "GHSX transfer failed.");
  },
});

const statusSocket =
  useStatusSocket({

    socket,

    myChatId: realMyChatId,

    setStatusNotifications,

    setMyStatusIds,
    setHasUnreadStatus, 
onViewsUpdated: (
  statusId,
  viewerChatId,
  viewsCount
) => {

  setStatusNotifications((prev) =>
    prev.map((status) =>
      status.statusId === statusId
        ? {
            ...status,
            viewsCount,
          }
        : status
    )
  );

},
  });

  /* ─────────────────────────────────────────────
   🎬 MEDIA SOCKET
   New TikTok-style media post/comment system
───────────────────────────────────────────── */
const mediaSocket = useMediaSocket({
  socket,

  /* ================= OPTIMISTIC ================= */
mediaOnOptimisticPost: (post) => {
  const add = (prev) =>
    prev.some((p) => String(p.postId) === String(post.postId))
      ? prev
      : [post, ...prev];

  setGlobalMediaLivePosts(add);
  setFollowingMediaPosts(add);
  setMyMediaPosts(add);
},

  /* ================= POSTS ================= */
mediaOnNewGlobalPost: (post) => {
  const upsert = (prev) =>
    prev.some((p) => String(p.postId) === String(post.postId))
      ? prev.map((p) =>
          String(p.postId) === String(post.postId)
            ? { ...p, ...post, isOptimistic: false }
            : p
        )
      : [post, ...prev];

  setGlobalMediaLivePosts(upsert);
   setMyMediaPosts((prev) =>
    String(post?.author?.chatId) === String(realMyChatId) ? upsert(prev) : prev
  );
  setFollowingMediaPosts((prev) =>
    prev.some((p) => String(p.postId) === String(post.postId))
      ? upsert(prev)
      : prev
  );
},

mediaOnNewFollowingPost: (post) =>
  setFollowingMediaPosts((prev) =>
    prev.some((p) => String(p.postId) === String(post.postId))
      ? prev.map((p) =>
          String(p.postId) === String(post.postId)
            ? { ...p, ...post, isOptimistic: false }
            : p
        )
      : [post, ...prev]
  ),
  /* ================= LIKES ================= */

  mediaOnLikeUpdate: ({ postId, likesCount, action, actorChatId }) => {
    const updatePosts = (setter) =>
      setter((prev) =>
        prev.map((p) =>
          String(p.postId) === String(postId)
            ? {
                ...p,
                likesCount,
                likedByMe:
                  actorChatId === realMyChatId
                    ? action === "like"
                    : p.likedByMe,
              }
            : p
        )
      );

    updatePosts(setGlobalMediaLivePosts);
    updatePosts(setFollowingMediaPosts);
    updatePosts(setCommentedMediaPosts);
    updatePosts(setMyMediaPosts);
  },

  /* ================= COMMENTS ================= */

  mediaOnNewComment: ({ postId, comment }) => {
    if (!comment) return;

    setMediaCommentsByPost((prev) => {
      const existing = prev[postId] || [];

      if (existing.some((c) => String(c.commentId) === String(comment.commentId))) {
        return prev;
      }

      return { ...prev, [postId]: [...existing, comment] };
    });
  },
  mediaOnCommentCountUpdate: ({
    postId,
    postCommentsCount,
    parentCommentId,
    parentCommentCommentsCount,
  }) => {
    if (typeof postCommentsCount === "number") {
      const updatePosts = (setter) =>
        setter((prev) =>
          prev.map((p) =>
            String(p.postId) === String(postId)
              ? { ...p, commentsCount: postCommentsCount }
              : p
          )
        );

      updatePosts(setGlobalMediaLivePosts);
      updatePosts(setFollowingMediaPosts);
      updatePosts(setCommentedMediaPosts);
      updatePosts(setMyMediaPosts);
    }

    if (
      parentCommentId &&
      typeof parentCommentCommentsCount === "number"
    ) {
      setMediaCommentsByPost((prev) => ({
        ...prev,
        [postId]: (prev[postId] || []).map((c) =>
          String(c.commentId) === String(parentCommentId)
            ? { ...c, commentsCount: parentCommentCommentsCount }
            : c
        ),
      }));
    }
  },

  /* ================= NOTIFICATIONS ================= */

  mediaOnUnreadLikeUpdate: (payload) => {
    setMediaNotifications((prev) => {
      const filtered = prev.filter(
        (n) =>
          !(
            n.type === "media_post_like" &&
            String(n.postId) === String(payload.postId)
          )
      );

      return [
        { type: "media_post_like", ...payload },
        ...filtered,
      ];
    });
  },

  mediaOnNotificationsCleared: () => {
    setMediaNotifications([]);
  },

  /* ================= VIEWS ================= */

  mediaOnViewUpdate: ({ postId, viewsCount }) => {
    const updatePosts = (setter) =>
      setter((prev) =>
        prev.map((p) =>
          String(p.postId) === String(postId)
            ? { ...p, viewsCount }
            : p
        )
      );

    updatePosts(setGlobalMediaLivePosts);
    updatePosts(setFollowingMediaPosts);
    updatePosts(setCommentedMediaPosts);
    updatePosts(setMyMediaPosts);
  },
  /* ================= COMMENT LIKES ================= */

  mediaOnCommentLikeUpdate: ({ commentId, likesCount }) => {
    setMediaCommentsByPost((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((pid) => {
        updated[pid] = updated[pid].map((c) =>
          String(c.commentId) === String(commentId)
            ? { ...c, likesCount }
            : c
        );
      });

      return updated;
    });
  },

  /* ================= COMMENT VIEWS ================= */

  mediaOnCommentViewUpdate: ({ commentId, viewsCount }) => {
    setMediaCommentsByPost((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((pid) => {
        updated[pid] = updated[pid].map((c) =>
          String(c.commentId) === String(commentId)
            ? { ...c, viewsCount }
            : c
        );
      });

      return updated;
    });
  },
});

  /* ═══════════════════════════════════════
     STEP 8 — FEED SOCKET
  ═══════════════════════════════════════ */

  const location        = useLocation();
  const navigate        = useNavigate();
  const isPostPage      = location.state?.view === "post" ||
    Boolean(new URLSearchParams(location.search).get("post"));
  const isAppInvitePage = location.pathname === "/invite";
  const inviteToken     = sessionStorage.getItem("pendingInviteToken");
   // Strip ?tab=buy-data after it has been read by the activeTab initializer,
  // so a refresh doesn't keep forcing the Buy Data tab.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("tab")) {
      navigate(location.pathname, { replace: true });
    }
  }, []); // eslint-disable-line

  const updateAllFeeds = (updater) => {
    setGlobalLivePosts(updater);
    setFeedPosts(updater);
    setInteractionPosts(updater);
  };

  const upsertNotification = (
  prev,
  newItem
) => {

  /* ==========================================
     SUPPORT NOTIFICATIONS
     ========================================== */

  if (
    newItem.type === "post_support" ||
    newItem.type === "comment_support"
  ) {

    const index =
      prev.findIndex(
        n =>
          String(
            n.notificationId ||
            n.id
          ) ===
          String(
            newItem.notificationId ||
            newItem.id
          )
      );

    // existing support notification
    if (index !== -1) {

      const updated = [...prev];

      updated[index] = {

        ...updated[index],

        ...newItem,

        // preserve true if server says read
        isRead:
          newItem.isRead ??
          updated[index].isRead,

      };

      return updated;
    }

    // brand-new support notification
    return [
      newItem,
      ...prev,
    ];
  }

  /* ==========================================
     NORMAL NOTIFICATIONS
     ========================================== */

  const index =
    prev.findIndex(
      n =>
        String(n.type) ===
          String(newItem.type) &&
        String(n.postId) ===
          String(newItem.postId) &&
        String(n.commentId || "") ===
          String(newItem.commentId || "")
    );

  // new notification
  if (index === -1) {

    return [
      newItem,
      ...prev,
    ];
  }

  // update existing notification
  const updated = [...prev];

  updated[index] = {

    ...updated[index],

    ...newItem,

    isRead:
      newItem.isRead ??
      updated[index].isRead,

  };

  return updated;
};

  const feedSocket = useFeedSocket(socket, {
    chatId: realMyChatId,

    onOptimisticPost: (post) =>
      setGlobalLivePosts((prev) =>
        prev.some((p) => String(p.postId) === String(post.postId)) ? prev : [post, ...prev]
      ),

   onNewPost: (post) => {
  const isMine    = String(post?.author?.chatId) === String(realMyChatId);
  const alreadyIn = globalLivePosts.some((p) => String(p.postId) === String(post.postId));

  if (!isMine && !alreadyIn && activeTab !== TABS.FEED) {
    setUnreadFeedCount((c) => c + 1);
  }

  setGlobalLivePosts((prev) => {
    const exists = prev.some((p) => String(p.postId) === String(post.postId));
    return exists ? prev.map((p) => String(p.postId) === String(post.postId) ? post : p) : [post, ...prev];
  });
},

    onNewFollowingPost: (post) =>
      setFeedPosts((prev) => {
        const exists = prev.some((p) => String(p.postId) === String(post.postId));
        return exists ? prev.map((p) => String(p.postId) === String(post.postId) ? post : p) : [post, ...prev];
      }),

    onOptimisticComment: ({ postId, comment }) => {
      setCommentsByPost((prev) => ({ ...prev, [postId]: [...(prev[postId] || []), comment] }));
      const t = comment?.createdAt ? new Date(comment.createdAt).getTime() : Date.now();
      updateAllFeeds((posts) =>
        posts.map((p) =>
          String(p.postId) === String(postId)
            ? { ...p, hasCommented: true, latestCommentPreview: { username: comment.author?.username, text: comment.text, time: t, newCount: p.latestCommentPreview?.newCount || 0 } }
            : p
        )
      );
      setInteractionActivity((prev) => ({ ...prev, [postId]: t }));
    },

   onNewComment: ({ postId, comment, postCommentsCount, parentCommentCommentsCount, parentCommentId, removeOptimisticCommentId }) => {
  if (removeOptimisticCommentId) {
    setCommentsByPost((prev) => {
      const u = { ...prev };
      Object.keys(u).forEach((pid) => {
        u[pid] = u[pid].filter((c) => String(c.commentId) !== String(removeOptimisticCommentId));
      });
      return u;
    });
    // ← DO NOT return here. Fall through so the real comment still gets added.
  }
      if (typeof postCommentsCount === "number") {
        updateAllFeeds((posts) =>
          posts.map((p) => String(p.postId) === String(postId) ? { ...p, commentsCount: postCommentsCount } : p)
        );
      }
      if (parentCommentId && typeof parentCommentCommentsCount === "number") {
        setCommentsByPost((prev) => ({
          ...prev,
          [postId]: (prev[postId] || []).map((c) =>
            String(c.commentId) === String(parentCommentId) ? { ...c, commentsCount: parentCommentCommentsCount } : c
          ),
        }));
      }
      if (!comment) return;
      const t = comment?.createdAt ? new Date(comment.createdAt).getTime() : Date.now();
      updateAllFeeds((posts) =>
        posts.map((p) =>
          String(p.postId) === String(postId)
            ? { ...p, hasCommented: true, latestCommentPreview: { commentId: comment.commentId, username: comment.author?.username, text: comment.text, time: t, newCount: p.latestCommentPreview?.newCount || 0 } }
            : p
        )
      );
      setInteractionActivity((prev) => ({ ...prev, [postId]: t }));
      setInteractionCounts((prev) => ({ ...prev, [postId]: prev[postId] ?? 0 }));
      setCommentsByPost((prev) => {
        const existing = prev[postId] || [];
        if (existing.some((c) => String(c.commentId) === String(comment.commentId))) return prev;
        return { ...prev, [postId]: [...existing, comment] };
      });
    },

    onNewCommentRepost: (repost) =>
      setGlobalLivePosts((prev) =>
        prev.some((p) => String(p.commentId) === String(repost.commentId)) ? prev : [repost, ...prev]
      ),

    onLikeUpdate: ({ postId, likesCount, action, actorChatId }) =>
      updateAllFeeds((posts) =>
        posts.map((p) =>
          String(p.postId) === String(postId)
            ? { ...p, likesCount, likedByMe: actorChatId === realMyChatId ? action === "like" : p.likedByMe }
            : p
        )
      ),

    onUnreadLikeUpdate: ({ postId, unreadCount, actorChatId, activityAt, previewText }) => {
      if (activeTab === TABS.NOTIFICATIONS) {
        feedSocketRef.current?.clearAllNotifications?.();
        unreadCount = 0;
      }
      setNotificationCounts((prev) =>
        upsertNotification(prev, { type: "post_like", postId, unreadCount, lastActivityAt: activityAt, actorChatId, previewText })
      );
    },

    onUnreadRepostUpdate: ({ postId, unreadCount, actorChatId, activityAt, isQuote, previewText }) => {
      if (activeTab === TABS.NOTIFICATIONS) {
        feedSocketRef.current?.clearAllNotifications?.();
        unreadCount = 0;
      }
      setNotificationCounts((prev) =>
        upsertNotification(prev, { type: isQuote ? "post_quote" : "post_repost", postId, unreadCount, lastActivityAt: activityAt, actorChatId, isQuote, previewText })
      );
    },

    onUnreadCommentLikeUpdate: ({ commentId, unreadCount, actorChatId, activityAt, postId, previewText, cleared }) => {
      if (!postId) return;
      if (cleared || unreadCount === 0) {
        setNotificationCounts((prev) =>
          upsertNotification(prev, { type: "comment_like", postId, commentId, unreadCount: 0, lastActivityAt: activityAt, actorChatId, previewText })
        );
        return;
      }
      if (activeTab === TABS.NOTIFICATIONS) {
        feedSocketRef.current?.clearAllNotifications?.();
        unreadCount = 0;
      }
      setNotificationCounts((prev) =>
        upsertNotification(prev, { type: "comment_like", postId, commentId, unreadCount, lastActivityAt: activityAt, actorChatId, previewText })
      );
    },

    onUnreadCommentRepostUpdate: ({ commentId, unreadCount, actorChatId, activityAt, postId, isQuote, previewText }) => {
      if (!postId) return;
      if (unreadCount === 0) {
        setNotificationCounts((prev) =>
          upsertNotification(prev, { type: isQuote ? "comment_quote" : "comment_repost", postId, commentId, unreadCount: 0, lastActivityAt: activityAt, actorChatId, isQuote, previewText })
        );
        return;
      }
      if (activeTab === TABS.NOTIFICATIONS) {
        feedSocketRef.current?.clearAllNotifications?.();
        unreadCount = 0;
      }
      setNotificationCounts((prev) =>
        upsertNotification(prev, { type: isQuote ? "comment_quote" : "comment_repost", postId, commentId, unreadCount, lastActivityAt: activityAt, actorChatId, isQuote, previewText })
      );
    },

    onCommentRepostCleared: ({ commentId }) =>
      setNotificationCounts((prev) =>
        prev.map((n) =>
          (n.type === "comment_repost" || n.type === "comment_quote") && String(n.commentId) === String(commentId)
            ? { ...n, unreadCount: 0 } : n
        )
      ),

    onLikeCleared: (postId) => {
      if (postId === "__ALL__") {
        setNotificationCounts((prev) => prev.map((n) => n.type === "post_like" ? { ...n, unreadCount: 0 } : n));
        return;
      }
      setNotificationCounts((prev) =>
        prev.map((n) => n.type === "post_like" && String(n.postId) === String(postId) ? { ...n, unreadCount: 0 } : n)
      );
    },

    onRepostCleared: (postId) => {
      if (postId === "__ALL__") {
        setNotificationCounts((prev) => prev.map((n) => (n.type === "post_repost" || n.type === "post_quote") ? { ...n, unreadCount: 0 } : n));
        return;
      }
      setNotificationCounts((prev) =>
        prev.map((n) =>
          (n.type === "post_repost" || n.type === "post_quote") && String(n.postId) === String(postId)
            ? { ...n, unreadCount: 0 } : n
        )
      );
    },

    onCommentLikeUpdate: ({ commentId, likesCount, action, actorChatId }) =>
      setCommentsByPost((prev) => {
        const u = { ...prev };
        Object.keys(u).forEach((pid) => {
          u[pid] = u[pid].map((c) =>
            String(c.commentId) === String(commentId)
              ? { ...c, likesCount, likedByMe: actorChatId === realMyChatId ? action === "like" : c.likedByMe }
              : c
          );
        });
        return u;
      }),

    onCommentRepostCountUpdate: ({ commentId, repostsCount }) =>
      setCommentsByPost((prev) => {
        const u = { ...prev };
        Object.keys(u).forEach((pid) => {
          u[pid] = u[pid].map((c) => String(c.commentId) === String(commentId) ? { ...c, repostsCount } : c);
        });
        return u;
      }),

    onViewUpdate: ({ postId, viewsCount }) =>
      updateAllFeeds((posts) =>
        posts.map((p) => String(p.postId) === String(postId) ? { ...p, viewsCount } : p)
      ),

    onCommentViewUpdate: ({ commentId, viewsCount }) =>
      setCommentsByPost((prev) => {
        const u = { ...prev };
        Object.keys(u).forEach((pid) => {
          u[pid] = u[pid].map((c) => String(c.commentId) === String(commentId) ? { ...c, viewsCount } : c);
        });
        return u;
      }),

    onRepostCountUpdate: ({ postId, repostsCount }) =>
      updateAllFeeds((posts) =>
        posts.map((p) => String(p.postId) === String(postId) ? { ...p, repostsCount } : p)
      ),

    onFollowSuccess: ({ targetChatId, followersCount }) => {
      const followed = nearbyUsers.find((u) => String(u.chatId) === String(targetChatId));
      setUsers((prev) => {
        if (prev.some((u) => String(u.chatId) === String(targetChatId)))
          return prev.map((u) => String(u.chatId) === String(targetChatId) ? { ...u, isFollowing: true, followersCount } : u);
        return followed ? [{ ...followed, isFollowing: true, unreadCount: 0, lastActivityAt: new Date() }, ...prev] : prev;
      });
      setNearbyUsers((prev) =>
        prev.map((u) => String(u.chatId) === String(targetChatId) ? { ...u, isFollowing: true, followersCount } : u)
      );
    },

    onInteractionCleared: (postId) => {
      setInteractionCounts((prev) => ({ ...prev, [postId]: 0 }));
      updateAllFeeds((posts) =>
        posts.map((p) =>
          String(p.postId) === String(postId)
            ? { ...p, latestCommentPreview: { ...p.latestCommentPreview, newCount: 0 } }
            : p
        )
      );
    },

   onUnreadUpdate: (
  postId,
  unreadCount,
  activityAt,
  postPreview
) => {

  const time =
    Number(activityAt) ||
    Date.now();

  const viewingPostId =
    location.state?.post?.postId;

  if (
    isPostPage &&
    String(viewingPostId) ===
    String(postId)
  ) {

    feedSocketRef.current
      ?.clearInteractionUnread?.({
        postId,
        chatId: realMyChatId,
      });

    unreadCount = 0;
  }

  setInteractionCounts((prev) => ({
    ...prev,
    [postId]: unreadCount,
  }));

  setInteractionActivity((prev) => ({
    ...prev,
    [postId]: time,
  }));

  updateAllFeeds((posts) =>
    posts.map((p) =>
      String(p.postId) === String(postId)
        ? {
            ...p,
            hasCommented: true,
            latestCommentPreview: {
              commentId:
                postPreview?.commentId,
              username:
                postPreview?.authorUsername,
              text:
                postPreview?.text,
              time,
              newCount:
                unreadCount,
            },
          }
        : p
    )
  );

  // 🔥 ADD THIS BLOCK

  setInteractionPosts((prev) => {

    const exists =
      prev.some(
        (p) =>
          String(p.postId) ===
          String(postId)
      );

    if (exists) {
      return prev.map((p) =>
        String(p.postId) ===
        String(postId)
          ? {
              ...p,
              hasCommented: true,
              latestCommentPreview: {
                commentId:
                  postPreview?.commentId,
                username:
                  postPreview?.authorUsername,
                text:
                  postPreview?.text,
                time,
                newCount:
                  unreadCount,
              },
            }
          : p
      );
    }

    const sourcePost =

      globalLivePosts.find(
        (p) =>
          String(p.postId) ===
          String(postId)
      ) ||

      feedPosts.find(
        (p) =>
          String(p.postId) ===
          String(postId)
      );

    if (!sourcePost) {
      return prev;
    }

    return [
      {
        ...sourcePost,
        hasCommented: true,
        latestCommentPreview: {
          commentId:
            postPreview?.commentId,
          username:
            postPreview?.authorUsername,
          text:
            postPreview?.text,
          time,
          newCount:
            unreadCount,
        },
      },
      ...prev,
    ];
  });

},

    onFollowedYou: ({ chatId }) => {
      const follower = users.find((u) => String(u.chatId) === String(chatId)) || nearbyUsers.find((u) => String(u.chatId) === String(chatId));
      if (!follower) return;
      setUsers((prev) => prev.some((u) => String(u.chatId) === String(chatId)) ? prev : [{ ...follower, unreadCount: 0, lastActivityAt: new Date() }, ...prev]);
    },

    onUnfollowedYou: ({ chatId }) =>
      setUsers((prev) => prev.map((u) => String(u.chatId) === String(chatId) ? { ...u, isFollowing: false } : u)),

    onUnfollowSuccess: ({ targetChatId, followersCount }) => {
      const upd = (list) => list.map((u) => String(u.chatId) === String(targetChatId) ? { ...u, isFollowing: false, followersCount } : u);
      setUsers(upd);
      setNearbyUsers(upd);
    },

    onFollowCounts: ({ chatId, followersCount, followingCount }) => {
      const upd = (list) => list.map((u) => String(u.chatId) === String(chatId) ? { ...u, followersCount, followingCount } : u);
      setUsers(upd);
      setNearbyUsers(upd);
      if (String(chatId) === String(realMyChatId)) {
        setCurrentUser((prev) => ({
          ...prev,
          chatInfo: prev.chatInfo.map((ci) =>
            String(ci.chatId) === String(chatId) ? { ...ci, followersCount, followingCount } : ci
          ),
        }));
      }
    },

    onPostSupported: ({ postId, amount, senderChatId, txHash, wallet }) => {
      // Update the USWD wallet balance when someone tips your post
      if (wallet) setUswdWallet(wallet);
      toast.success(`You received ${amount} USWD for your post!`);
    },

    onCommentSupported: ({ commentId, postId, amount, senderChatId, txHash, wallet }) => {
      // Update the USWD wallet balance when someone tips your comment
      if (wallet) setUswdWallet(wallet);
      toast.success(`You received ${amount} USWD for your comment!`);
    },

    onSupportCountUpdate: ({ postId, supportsCount, supportsAmount }) =>
  updateAllFeeds((posts) =>
    posts.map((p) =>
      String(p.postId) === String(postId)
        ? { ...p, supportsCount, supportsAmount }
        : p
    )
  ),

onCommentSupportCountUpdate: ({ commentId, supportsCount, supportsAmount }) =>
  setCommentsByPost((prev) => {
    const u = { ...prev };
    Object.keys(u).forEach((pid) => {
      u[pid] = u[pid].map((c) =>
        String(c.commentId) === String(commentId)
          ? { ...c, supportsCount, supportsAmount }
          : c
      );
    });
    return u;
  }),

onUnreadSupportPost: ({  notificationId, postId, unreadCount, unreadAmount, activityAt, senderChatId, previewText }) => {
  if (activeTab === TABS.NOTIFICATIONS) {
    feedSocketRef.current?.clearAllNotifications?.();
    unreadCount = 0;
  }
  setNotificationCounts((prev) =>
    upsertNotification(prev, {
       notificationId,
      type: "post_support",
      postId,
      unreadCount,
      unreadAmount,
      lastActivityAt: activityAt,
      actorChatId: senderChatId,
      previewText,
    })
  );
},

onUnreadSupportComment: ({
  notificationId,
  commentId,
  postId,
  unreadCount,
  unreadAmount,
  activityAt,
  senderChatId,
  previewText,
}) => {

  if (activeTab === TABS.NOTIFICATIONS) {

    feedSocketRef.current
      ?.clearAllNotifications?.();

    unreadCount = 0;
  }

  setNotificationCounts((prev) =>
    upsertNotification(prev, {
      notificationId,
      type: "comment_support",
      postId,
      commentId,
      unreadCount,
      unreadAmount,
      lastActivityAt: activityAt,
      actorChatId: senderChatId,
      previewText,
    })
  );
},

/* ==========================================
   SUPPORT CLEARED
========================================== */

onSupportPostCleared: ({
  postId,
  isRead,
}) => {

  setNotificationCounts((prev) =>
    prev.map((n) =>

      n.type === "post_support" &&
      String(n.postId) === String(postId)

        ? {
            ...n,
            isRead,
          }

        : n
    )
  );

},

onSupportCommentCleared: ({
  commentId,
  isRead,
}) => {

  setNotificationCounts((prev) =>
    prev.map((n) =>

      n.type === "comment_support" &&
      String(n.commentId) === String(commentId)

        ? {
            ...n,
            isRead,
          }

        : n
    )
  );

},

onSupportCleared: (postId) => {

  if (postId === "__ALL__") {

    setNotificationCounts((prev) =>
      prev.map((n) =>

        (
          n.type === "post_support" ||
          n.type === "comment_support"
        )

          ? {
              ...n,
              unreadCount: 0,
            }

          : n
      )
    );
  }
},


    onError: (err) => toast.error(err?.message || "Feed error"),
  });

  feedSocketRef.current = feedSocket;

  /* ═══════════════════════════════════════
     STEP 9 — FEED ACTION SOCKET + USER SOCKET
  ═══════════════════════════════════════ */
   const feedActionSocket = useFeedActionSocket(socket, {
    chatId: realMyChatId,
    setFeedPosts: updateAllFeeds,
    setComments: (updater) =>
      setCommentsByPost((prev) => {
        const u = { ...prev };
        Object.keys(u).forEach((pid) => {
          u[pid] = typeof updater === "function" ? updater(u[pid] || []) : u[pid];
        });
        return u;
      }),
  });

  const userSocket = useUserSocket(socket, {
    onUsersAll: (incoming) => {
      const normalised = incoming.map((u) => {
        if (!u.lastMessage || typeof u.lastMessage === "string") return u;
        const text     = u.lastMessage.text ?? "";
        const hasMedia = !!u.lastMessage.media?.url;
        return { ...u, lastMessage: text || (hasMedia ? "📎 Media" : "") };
      });
      previewDecryptDoneRef.current = false;
      setUsers(normalised);
    },
    onUsersNearby: (u) => setNearbyUsers(u),
    onUsersError:  (err) => toast.error(err?.message || "Failed to load users"),
  });

  useEffect(() => { if (socket) userSocket.getAllUsers(); }, [socket]);

  /* ═══════════════════════════════════════
     STEP 10 — useEFFECTS
  ═══════════════════════════════════════ */
/* Responsive */
  useEffect(() => {
    const resize = () => setMobileView(window.innerWidth <= 768);
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  /* Follow device dark-mode setting, unless the user manually
     picked a theme via the Settings toggle (localStorage "themeSource"). */
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    const applySystemTheme = () => {
      // Only auto-switch if the user hasn't manually overridden it
      if (localStorage.getItem("themeSource") === "manual") return;
      const next = media.matches ? "dark" : "light";
      setTheme(next);
      localStorage.setItem("theme", next);
    };

    // Modern browsers
    if (media.addEventListener) {
      media.addEventListener("change", applySystemTheme);
    } else if (media.addListener) {
      // Safari <14 / some Android & embedded WebViews only support this legacy API
      media.addListener(applySystemTheme);
    }

    // Fallback: some mobile WebViews don't fire "change" while the app
    // is backgrounded — re-check whenever the app/tab regains focus,
    // so the OS switch is picked up the moment you come back to it.
    document.addEventListener("visibilitychange", applySystemTheme);
    window.addEventListener("focus", applySystemTheme);
    window.addEventListener("pageshow", applySystemTheme);

    // Run once on mount too, in case the OS theme changed between
    // app launches and localStorage still has the stale value.
    applySystemTheme();

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", applySystemTheme);
      } else if (media.removeListener) {
        media.removeListener(applySystemTheme);
      }
      document.removeEventListener("visibilitychange", applySystemTheme);
      window.removeEventListener("focus", applySystemTheme);
      window.removeEventListener("pageshow", applySystemTheme);
    };
  }, []);

 
useEffect(() => {
  if (!Array.isArray(p2pOrders)) return;
  if (!realMyChatId) return;

  const latestBuyerOrder = p2pOrders
    .filter((o) =>
      ["pending", "paid"].includes(o.status) &&
      String(o.buyerChatId) === String(realMyChatId)
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt || 0) -
        new Date(a.createdAt || 0)
    )[0];
if (!latestBuyerOrder) {
  // No active buyer order remains.
  // Clear the stale payment session and return to the P2P home page.
  if (activeP2POrder) {
    setActiveP2POrder(null);
  }

  if (activeP2PPage === "payment") {
    setActiveP2PPage("market");
  }

  return;
}

  if (
    !activeP2POrder ||
    activeP2POrder.orderId !== latestBuyerOrder.orderId ||
    activeP2POrder.status !== latestBuyerOrder.status
  ) {
    setActiveP2POrder(latestBuyerOrder);
    setActiveP2PPage("payment");
  }
}, [p2pOrders, activeP2POrder, realMyChatId]);

// ─────────────────────────────────────────────────────────────────────────────
// Replace the entire "🔥 WORKER RESPONSE HANDLER" useEffect in GroupChat.js
// with this block.
// ─────────────────────────────────────────────────────────────────────────────

useEffect(() => {
  const worker = decryptWorkerRef.current;
  if (!worker) return;

  const handler = (e) => {
    const { type, payload } = e.data;

      if (type === "DECRYPT_DONE") {
      const { chatKey, messages: decrypted } = payload;

      if (!decrypted?.length) return;

     const sorted = [...decrypted].sort(
  (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
);


/* Normalize every decrypted message immediately. */
const formatted = sorted.map((m) => ({
  ...m,
  chatKey,

  id:
    m.id ||
    m.msgId ||
    m.messageId ||
    m.raw?.key?.msgId,

  text:
    m.text ??
    m.raw?.message?.conversation ??
    m.raw?.message?.extendedTextMessage?.text ??
    "",

  senderChatId:
    m.senderChatId ??
    m.raw?.key?.participant ??
    m.raw?.key?.remoteJid,

  timestamp:
    m.timestamp ??
    m.raw?.messageTimestamp ??
    Date.now(),

  media:
    m.media ??
    m.raw?.message?.imageMessage ??
    m.raw?.message?.videoMessage ??
    m.raw?.message?.documentMessage ??
    m.raw?.message?.audioMessage ??
    null,
}));

// Merge decrypted messages without overwriting existing content
// with empty text or missing media.
setMessages((prev) => {
  const existing = prev[chatKey] || [];
  const map = new Map();

  const getMessageKey = (m) =>
    String(
      m.id ||
      m.msgId ||
      m.messageId ||
      [
        m.timestamp || "",
        m.senderChatId || "",
        m.encryptedText?.ciphertext ||
          m.forRecipient?.ciphertext ||
          m.forSender?.ciphertext ||
          m.text ||
          m.media?.url ||
          m.messageType ||
          "message",
      ].join("-")
    );

  [...existing, ...formatted].forEach((m) => {
    const key = getMessageKey(m);
    const previous = map.get(key);

    if (!previous) {
      map.set(key, m);
      return;
    }

    map.set(key, {
      ...previous,
      ...m,

      // Preserve readable content if the new result is incomplete.
      text:
        typeof m.text === "string" && m.text.trim() !== ""
          ? m.text
          : previous.text ?? "",

      // Preserve existing media when the worker returns none.
      media: m.media ?? previous.media ?? null,

      // Preserve reply information when it is absent in the new result.
      replyPreview:
        m.replyPreview ?? previous.replyPreview ?? null,
    });
  });

  const merged = Array.from(map.values()).sort(
    (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
  );

  return {
    ...prev,
    [chatKey]: merged,
  };
});

      // ── Compute sidebar preview ───────────────────────────────────────────
      const now = Date.now();

      // Latest PENDING scheduled message sent by ME
      const latestScheduled = [...sorted]
        .reverse()
        .find(
          (m) =>
            m._scheduled &&
            m.scheduledAt &&
            new Date(m.scheduledAt).getTime() > now &&
            String(m.senderChatId) === String(realMyChatId)
        );

      // Latest NORMAL (non-scheduled) message
    // AFTER — replace with:
// Latest NORMAL (non-scheduled) message, skipping messages deleted for me
const latestNormal = [...sorted]
  .reverse()
  .find((m) => {
    if (m._scheduled) return false;
    // Skip messages deleted-for-me (IDB stores deletedFor array)
    if (Array.isArray(m.deletedFor) && m.deletedFor.includes(String(realMyChatId))) return false;
    if (m.hiddenForMe) return false;
    return true;
  });
      const scheduledTs = latestScheduled ? (latestScheduled.timestamp || 0) : -1;
      const normalTs    = latestNormal    ? (latestNormal.timestamp    || 0) : -1;

      let previewPayload;
      let previewTime;

      if (latestScheduled && scheduledTs >= normalTs) {
        previewPayload = {
          text:        latestScheduled.text || (latestScheduled.media ? "📎 Media" : ""),
          isDeleted:   false,
          isScheduled: true,
          scheduledAt: new Date(latestScheduled.scheduledAt).toISOString(),
        };
        previewTime = scheduledTs;
     // AFTER — replace with:
} else {
  // If no valid normal message exists (all deleted-for-me), show empty preview
  if (!latestNormal) {
    previewPayload = { text: "", isDeleted: false, isScheduled: false };
    previewTime = 0;
  } else {
    const latest = latestNormal;
    const isDeleted = latest.isDeleted;
    previewPayload = isDeleted
      ? {
          text: String(latest.senderChatId) === String(realMyChatId)
            ? "You deleted this message"
            : "This message was deleted",
          isDeleted:   true,
          isScheduled: false,
        }
      : {
          text:        latest.text || (latest.media ? "📎 Media" : ""),
          isDeleted:   false,
          isScheduled: false,
        };
    previewTime = latest.timestamp || Date.now();
  }
}
      // ── Write preview to users / groups (with pending-preview buffer) ──────
      if (chatKey.startsWith("user-")) {
        const chatId = chatKey.replace("user-", "");

        setUsers((prev) => {
          if (!prev.length) {
            pendingPreviewsRef.current = [
              ...pendingPreviewsRef.current.filter((p) => p.chatKey !== chatKey),
              { chatKey, chatId, previewPayload, previewTime, type: "user" },
            ];
            return prev;
          }

          const match = prev.find((u) => String(u.chatId) === String(chatId));
          if (!match) {
            pendingPreviewsRef.current = [
              ...pendingPreviewsRef.current.filter((p) => p.chatKey !== chatKey),
              { chatKey, chatId, previewPayload, previewTime, type: "user" },
            ];
            return prev;
          }

          return prev.map((u) =>
            String(u.chatId) !== String(chatId) ? u : {
              ...u,
              lastMessage:    previewPayload,
              lastActivityAt: previewTime || u.lastActivityAt,
            }
          );
        });

      } else if (chatKey.startsWith("group-")) {
        const groupId = chatKey.replace("group-", "");

        setGroups((prev) => {
          if (!prev.length) {
            pendingPreviewsRef.current = [
              ...pendingPreviewsRef.current.filter((p) => p.chatKey !== chatKey),
              { chatKey, groupId, previewPayload, previewTime, type: "group" },
            ];
            return prev;
          }

          const match = prev.find((g) => String(g.groupId) === String(groupId));
          if (!match) {
            pendingPreviewsRef.current = [
              ...pendingPreviewsRef.current.filter((p) => p.chatKey !== chatKey),
              { chatKey, groupId, previewPayload, previewTime, type: "group" },
            ];
            return prev;
          }

          return prev.map((g) =>
            String(g.groupId) !== String(groupId) ? g : {
              ...g,
              lastMessage:    previewPayload,
              lastActivityAt: previewTime || g.lastActivityAt,
            }
          );
        });
      }
    }
  };

  worker.addEventListener("message", handler);
  return () => worker.removeEventListener("message", handler);
}, [realMyChatId]);

useEffect(() => {

  const worker =
    statusDecryptWorkerRef.current;

  if (!worker) return;

  worker.onmessage =
    async (event) => {

      if (

        event.data.type !==

        "STATUS_DECRYPT_DONE"

      ) {

        return;

      }

      const statuses =

        event.data.payload.statuses ||

        [];

      setStatusNotifications(
        statuses
      );

      await Promise.all(

        statuses.map(saveStatus)

      );

    };

}, []);

  /* ─────────────────────────────────────────────
     🔥 INITIAL IDB BOOTSTRAP (FETCH ONLY IF EMPTY)
     Sets bootstrapDone=true when finished so the
     hydration effect below knows it's safe to run.
  ───────────────────────────────────────────── */
  const idbHydratedRef = useRef(false);
  const hydrationSentRef  = useRef(false);

  const isIDBEmpty = async () => {
    const db    = await dbPromise;
    const count = await db.count("chat-messages");
    return count === 0;
  };


const isStatusIDBEmpty =
async () => {

  const db =
    await dbPromise;

  const count =
    await db.count(
      "statuses"
    );

  return count === 0;

};

 useEffect(() => {
  if (!chatMetaReady || !realMyChatId) {
    return;
  }
 
  if (idbHydratedRef.current) return;
  idbHydratedRef.current = true;
 
  let cancelled = false;
 
  (async () => {
    try {
      const empty = await isIDBEmpty();
  /* ── SCHEDULED MESSAGE SYNC (IDB ↔ SERVER) ── */
try {
  const scheduledFromIDB = await getAllScheduledMessages();

  let scheduledToHydrate = [];

  if (scheduledFromIDB.length > 0) {
    scheduledToHydrate = scheduledFromIDB;
  } else {
    const [dmList, groupList] = await Promise.all([
      fetchScheduledMessages(),
      fetchGroupScheduledMessages(),
    ]);
    const combined = [...dmList, ...groupList];

    if (combined.length > 0) {
      await Promise.all(combined.map((msg) => saveScheduledMessage(msg)));
      scheduledToHydrate = combined;
    } else {
    }
  }

  if (scheduledToHydrate.length > 0) {
    /* 1. Pre-load crypto material */
    const worker = decryptWorkerRef.current;
    let myPrivateKey = null;
    const groupKeyMap = {};

    if (worker && cryptoFns) {
      try {
        myPrivateKey = await cryptoFns.loadPrivateKey(realMyChatId).catch(() => null);
      } catch (_) {}

      for (const msg of scheduledToHydrate) {
        if (msg.chatKey?.startsWith("group-")) {
          const groupId = msg.chatKey.replace("group-", "");
          if (!groupKeyMap[groupId]) {
            try {
              const key = await cryptoFns.loadGroupKey(groupId);
              if (key) groupKeyMap[groupId] = key;
            } catch (_) {}
          }
        }
      }
    }

    /* 2. Group by chatKey, send each batch to worker for decryption.
          The existing DECRYPT_DONE handler will merge them into messages
          state — but we also need them in scheduledMessages state so
          ChatWindow → useScheduledMessage can show countdown bubbles.  */
    const byChatKey = {};
    for (const msg of scheduledToHydrate) {
      const key = msg.chatKey;
      if (!key) continue;
      if (!byChatKey[key]) byChatKey[key] = [];
      byChatKey[key].push({
        ...msg,
        id:         msg.msgId || msg.id,
        _scheduled: true,
      });
    }

    for (const [chatKey, msgs] of Object.entries(byChatKey)) {
      if (worker) {
        worker.postMessage({
          type: "DECRYPT_MESSAGES",
          payload: {
            chatKey,
            messages:   msgs,
            privateKey: myPrivateKey,
            groupKeyMap,
            myChatId:   realMyChatId,
          },
        });
      }
    }

    /* 3. Also set scheduledMessages state so ChatWindow can pass it to
          useScheduledMessage as initialScheduled → countdown bubbles
          appear immediately even before DECRYPT_DONE fires.            */
    setScheduledMessages(
      scheduledToHydrate.map((m) => ({
        ...m,
        id:         m.msgId || m.id,
        _scheduled: true,
      }))
    );
  }

} catch (err) {
 
}
 
 /* ── CASE A: IDB is empty → full bootstrap fetch ── */
if (empty) {

  const allChatKeys = [];
  users.forEach((u)  => { if (u.chatId)  allChatKeys.push(`user-${u.chatId}`);   });
  groups.forEach((g) => { if (g.groupId) allChatKeys.push(`group-${g.groupId}`); });

  // ── Pre-load crypto material once, before the loop ──
  const worker = decryptWorkerRef.current;
  let myPrivateKey = null;
  const groupKeyMap = {};

  if (worker && cryptoFns) {
    try {
      myPrivateKey = await cryptoFns.loadPrivateKey(realMyChatId).catch(() => null);
    } catch (_) {}

    for (const chatKey of allChatKeys) {
      if (chatKey.startsWith("group-")) {
        const groupId = chatKey.replace("group-", "");
        try {
          const key = await cryptoFns.loadGroupKey(groupId);
          if (key) groupKeyMap[groupId] = key;
        } catch (_) {}
      }
    }
  }

  // Mark hydration as sent NOW so the separate hydration effect
  // doesn't fire again when bootstrapDone flips to true.
  hydrationSentRef.current = true;

  const normalizeTimestamp = (t) => {
    if (!t) return Date.now();
    if (typeof t === "number") return t;
    const parsed = new Date(t).getTime();
    return isNaN(parsed) ? Date.now() : parsed;
  };

  for (const chatKey of allChatKeys) {
    if (cancelled) break;
    try {

      let fetchedMessages = [];

      if (chatKey.startsWith("user-")) {
        const peerChatId = chatKey.replace("user-", "");
        fetchedMessages  = await fetchChatHistory(peerChatId, realMyChatId);
      }

      if (chatKey.startsWith("group-")) {
        const groupId   = chatKey.replace("group-", "");
        fetchedMessages = await fetchGroupChatHistory(groupId, realMyChatId, socket);
      }

      if (!fetchedMessages?.length) continue;

      const records = fetchedMessages.map((m) => ({
        ...m,
        id:      m.msgId || m.id,
        chatKey,
      }));

      // ── 1. Send to worker immediately so React state updates NOW ──
      if (worker && (myPrivateKey || Object.keys(groupKeyMap).length > 0 || chatKey.startsWith("user-"))) {
        const normalizedMessages = records.map((m) => ({
          ...m,
          timestamp: normalizeTimestamp(m.timestamp),
        }));


        worker.postMessage({
          type: "DECRYPT_MESSAGES",
          payload: {
            chatKey,
            messages:    normalizedMessages,
            privateKey:  myPrivateKey,
            groupKeyMap,
            myChatId:    realMyChatId,
          },
        });
      }

    
      // ── 2. Save to IDB and wait for writes to finish ──
      await Promise.all(
        records.map((record) =>
          saveMessage(record).catch(() => null)
        )
      );

      // Yield to React between chats so UI can update progressively
      await new Promise((r) => setTimeout(r, 0));

    } catch (err) {
    }
  }

  return; // bootstrapDone set in finally
}
 
/* ── CASE B: IDB has data ── */

/*
 * IDB already contains chat data.
 * No cursor/delta synchronization is performed here.
 */
if (!justLoggedInRef.current) {
  return; // bootstrapDone set in finally
}

// Consume the flag immediately so a re-render cannot re-trigger this path.
justLoggedInRef.current = false;
 
    } catch (err) {
    
    } finally {
      if (!cancelled) {
        setBootstrapDone(true); // always signal done so hydration can proceed
      }
    }
  })();
 
  return () => { cancelled = true; };
}, [chatMetaReady, realMyChatId]);

useEffect(() => {

  if (!realMyChatId) return;

  if (statusHydratedRef.current)
    return;

  statusHydratedRef.current =
    true;

  let cancelled = false;

  (async () => {

    try {

      const empty =
        await isStatusIDBEmpty();

      if (!empty) {

        const cached =
          await loadStatuses();

        if (!cancelled) {

          setStatusNotifications(
            cached
          );

        }

      }

   const [

  mine,

  following,

] = await Promise.all([

  getMyStatuses(),

  getFollowingStatuses(),

]);

if (cancelled) return;

const encryptedStatuses = [

  ...(mine.statuses || []),

  ...(following.statuses || []),

];

/* =========================================
   RESTORE MISSING STATUS KEYS
========================================= */

const uniqueOwners = [

  ...new Set(

    encryptedStatuses

      .map(s => s.ownerChatId)

      .filter(Boolean)

  ),

];

await Promise.all(

  uniqueOwners.map(async (ownerChatId) => {

    try {

      await getStatusKey(ownerChatId);

    } catch (err) {

    }

  })

);

statusDecryptWorkerRef.current?.postMessage({

  type: "DECRYPT_STATUSES",

  payload: {

    statuses: encryptedStatuses,

  },

});

    }

    catch (err) {

    }

  })();

  return () => {

    cancelled = true;

  };

}, [

  realMyChatId,

]);

/* ─────────────────────────────────────────────
   🔥 FEED BOOTSTRAP (SERVER ONLY)
───────────────────────────────────────────── */
useEffect(() => {

  if (!realMyChatId) return;

  if (feedHydratedRef.current) return;
  feedHydratedRef.current = true;

  let cancelled = false;

  (async () => {

    try {

      setFeedLoading(true);
      setGlobalLoading(true);
      setInteractionLoading(true);

      const [
        followingResult,
        globalResult,
        interactionResult,
        myPostsResult,
        notificationHistory,
      ] = await Promise.all([
        fetchFeedHistory({ limit: 50 }),
        fetchGlobalFeedHistory({ limit: 50 }),
        fetchInteractionHistory({ limit: 50 }),
        fetchMyPostHistory({ limit: 50 }),
        fetchNotificationHistory({ limit: 100, skip: 0 }),
      ]);

      const followingFeed = followingResult?.posts || [];
      const globalFeed = globalResult?.posts || [];
      const interactionFeed = interactionResult?.posts || [];
      const myPostsFeed = myPostsResult?.posts || [];

      if (cancelled) return;

      /* ─────────────────────────────
         BUILD UNIQUE POSTS
      ───────────────────────────── */
      const allPosts = [
        ...(followingFeed || []),
        ...(globalFeed || []),
        ...(interactionFeed || []),
      ];

      const uniquePosts = Array.from(
        new Map(allPosts.map((p) => [p.postId, p])).values()
      );

      /* ─────────────────────────────
         LOAD COMMENTS
      ───────────────────────────── */
      const commentsMap = {};

      await Promise.all(
        uniquePosts.map(async (post) => {
          try {
            const result = await fetchPostWithComments(post.postId, {
              limit: 20,
              skip: 0,
            });

            commentsMap[post.postId] = result?.comments || [];

            setCommentSkip((prev) => ({
              ...prev,
              [post.postId]:
                result?.nextSkip || (result?.comments || []).length,
            }));

            setCommentHasMore((prev) => ({
              ...prev,
              [post.postId]: Boolean(result?.hasMore),
            }));
          } catch (err) {}
        })
      );

      if (cancelled) return;

      /* ====================================================
         HYDRATE REACT STATE
      ==================================================== */
      setFeedPosts(followingFeed || []);
      setGlobalLivePosts(globalFeed || []);
      setInteractionPosts(interactionFeed || []);

      setNotificationCounts(notificationHistory || []);

      setCommentsByPost(commentsMap);

      setFeedHasMore(followingResult?.hasMore ?? false);
      setGlobalHasMore(globalResult?.hasMore ?? false);
      setInteractionHasMore(interactionResult?.hasMore ?? false);
      setMyPostsHasMore(myPostsResult?.hasMore ?? false);

    } catch (err) {

    } finally {
      if (!cancelled) {
        setFeedLoading(false);
        setGlobalLoading(false);
        setInteractionLoading(false);
        setFeedBootstrapDone(true);
      }
    }

  })();

  return () => {
    cancelled = true;
  };

}, [realMyChatId]);

useEffect(() => {
  if (!chatMetaReady || !realMyChatId) return;
  if (!Array.isArray(scheduledMessages)) return;

  const now = Date.now();

  /* ─────────────────────────────────────────────
     1. FILTER VALID (NOT EXPIRED)
  ───────────────────────────────────────────── */
  const validScheduled = scheduledMessages.filter(
    (msg) => new Date(msg.scheduledAt).getTime() > now
  );

  if (!validScheduled.length) return;

  /* ─────────────────────────────────────────────
     2. BUILD latestByChatKey
  ───────────────────────────────────────────── */
  const latestByChatKey = {};

  for (const msg of validScheduled) {
    const chatKey = msg.chatKey;
    const msgId = msg.msgId || msg.id;
    const scheduledAt = msg.scheduledAt;

    if (!chatKey || !scheduledAt) continue;

    // Only show sender-side scheduled messages
    if (String(msg.senderChatId) !== String(realMyChatId)) continue;

    const existing = latestByChatKey[chatKey];

    if (
      !existing ||
      new Date(scheduledAt).getTime() >
        new Date(existing.scheduledAt).getTime()
    ) {
      latestByChatKey[chatKey] = { ...msg, msgId, scheduledAt };
    }
  }

  if (!Object.keys(latestByChatKey).length) return;

  /* ─────────────────────────────────────────────
     3. UPDATE USERS (BATCHED)
  ───────────────────────────────────────────── */
  setUsers((prev) =>
    prev.map((u) => {
      const chatKey = `user-${u.chatId}`;
      const scheduledMsg = latestByChatKey[chatKey];
      if (!scheduledMsg) return u;

      const scheduledTs = new Date(
        scheduledMsg.scheduledAt
      ).getTime();

      const currentTs = u.lastActivityAt
        ? new Date(u.lastActivityAt).getTime()
        : 0;

      if (scheduledTs < currentTs && !u.lastMessage?.isScheduled)
        return u;

      const chatMessages = messages[chatKey] || [];
      const match = chatMessages.find(
        (m) =>
          (m.id || m.msgId) === scheduledMsg.msgId && m._scheduled
      );

      const previewText = match?.text || "";

      return {
        ...u,
        lastMessage: {
          text:
            previewText ||
            (scheduledMsg.messageType === "media"
              ? "📎 Media"
              : ""),
          isDeleted: false,
          isScheduled: true,
          scheduledAt: new Date(
            scheduledMsg.scheduledAt
          ).toISOString(),
        },
        lastActivityAt: scheduledTs,
      };
    })
  );

  /* ─────────────────────────────────────────────
     4. UPDATE GROUPS (BATCHED)
  ───────────────────────────────────────────── */
  setGroups((prev) =>
    prev.map((g) => {
      const chatKey = `group-${g.groupId}`;
      const scheduledMsg = latestByChatKey[chatKey];
      if (!scheduledMsg) return g;

      const scheduledTs = new Date(
        scheduledMsg.scheduledAt
      ).getTime();

      const currentTs = g.lastActivityAt
        ? new Date(g.lastActivityAt).getTime()
        : 0;

      if (scheduledTs < currentTs && !g.lastMessage?.isScheduled)
        return g;

      const chatMessages = messages[chatKey] || [];
      const match = chatMessages.find(
        (m) =>
          (m.id || m.msgId) === scheduledMsg.msgId && m._scheduled
      );

      const previewText = match?.text || "";

      return {
        ...g,
        lastMessage: {
          text:
            previewText ||
            (scheduledMsg.messageType === "media"
              ? "📎 Media"
              : ""),
          isDeleted: false,
          isScheduled: true,
          scheduledAt: new Date(
            scheduledMsg.scheduledAt
          ).toISOString(),
        },
        lastActivityAt: scheduledTs,
      };
    })
  );

}, [
  scheduledMessages,
  chatMetaReady,
  realMyChatId,
  messages,
  setUsers,
  setGroups,
]);
  /* ─────────────────────────────────────────────
     🔥 PROGRESSIVE HYDRATION + WORKER
     Waits for bootstrapDone so IDB is guaranteed
     to have data before we scan and send to worker.
  ───────────────────────────────────────────── */
 useEffect(() => {
  if (!currentUser || !cryptoFns || !realMyChatId) {
   
    return;
  }

  if (!bootstrapDone) {
 
    return;
  }

  // 🔥 GUARD: only run once per login session
  if (hydrationSentRef.current) {
   
    return;
  }
  hydrationSentRef.current = true;

  let mounted = true;

  (async () => {
    try {
      const worker = decryptWorkerRef.current;
      if (!worker) return;

      const myPrivateKey = await cryptoFns
        .loadPrivateKey(realMyChatId)
        .catch(() => null);

     const db = await dbPromise;
const tx = db.transaction("chat-messages", "readonly");
const store = tx.objectStore("chat-messages");

const chatKeySet = new Set();
let cursor = await store.openCursor();

while (cursor) {
  if (cursor.value?.chatKey) {
    chatKeySet.add(cursor.value.chatKey);
  }
  cursor = await cursor.continue();
}

await tx.done;

const chatKeys = [...chatKeySet];

if (!chatKeys.length) return;

      const groupKeyMap = {};

for (const chatKey of chatKeys) {
        if (chatKey.startsWith("group-")) {
          const groupId = chatKey.replace("group-", "");
          const key     = await cryptoFns.loadGroupKey(groupId);
          if (key) groupKeyMap[groupId] = key;
        }
      }

      const normalizeTimestamp = (t) => {
        if (!t) return Date.now();
        if (typeof t === "number") return t;
        const parsed = new Date(t).getTime();
        return isNaN(parsed) ? Date.now() : parsed;
      };

      for (const chatKey of chatKeys) {
        if (!mounted) break;

        try {
const msgs = await loadMessages(chatKey);

if (!msgs?.length) continue;
const normalizedMessages = msgs.map((m) => ({
  ...m,
  timestamp: normalizeTimestamp(m.timestamp),
}));

/* Decrypt in the background. The DECRYPT_DONE
   handler will merge decrypted fields without
   leaving the chat empty first. */

worker.postMessage({
  type: "DECRYPT_MESSAGES",
  payload: {
    chatKey,
    messages: normalizedMessages,
    privateKey: myPrivateKey,
    groupKeyMap,
    myChatId: realMyChatId,
  },
});

/* Yield so React paints progressively. */
await new Promise((r) => setTimeout(r, 0));
        } catch (err) {
    
        }
      }

    } catch (err) {
  
    }
  })();

  return () => { mounted = false; };

}, [currentUser, cryptoFns, realMyChatId, bootstrapDone]);
  //   ↑ bootstrapDone is the key dep — fires once bootstrap finishes,
  //     whether IDB was pre-populated or freshly fetched.

  /* Clear Feed tab badge while the user is on Feed */
useEffect(() => {
  if (activeTab === TABS.FEED) setUnreadFeedCount(0);
}, [activeTab]);

   /* Auto-clear notification badges */
  useEffect(() => {
    if (!realMyChatId) return;
    if (activeTab !== TABS.NOTIFICATIONS) return;
    feedSocketRef.current?.clearAllNotifications?.();
    setNotificationCounts((prev) =>
      prev.map((n) => ({ ...n, unreadCount: 0, isRead: true }))
    );
  }, [activeTab, realMyChatId]);

/* Post page — load comments + clear unreads */
useEffect(() => {

  if (!isPostPage) {

    // Reset guard when leaving the post page
    postPageLoadedRef.current = null;

    return;
  }

  // Wait until feed bootstrap finished
  if (!feedBootstrapDone) return;

  const postId =
    location.state?.post?.postId;

  if (!postId) return;

  // Prevent duplicate hydration
  if (
    postPageLoadedRef.current ===
    postId
  ) {
    return;
  }

  postPageLoadedRef.current =
    postId;

  /* ====================================================
     CLEAR INTERACTION UNREAD
  ==================================================== */

  feedSocketRef.current
    ?.clearInteractionUnread?.({
      postId,
      chatId: realMyChatId,
    });

  setInteractionCounts((prev) => ({
    ...prev,
    [postId]: 0,
  }));

  updateAllFeeds((posts) =>
    posts.map((p) =>
      String(p.postId) ===
      String(postId)
        ? {
            ...p,
            latestCommentPreview: {
              ...p.latestCommentPreview,
              newCount: 0,
            },
          }
        : p
    )
  );

  let mounted = true;

  (async () => {

      try {

      /* ====================================================
         1. TRY MEMORY FIRST (post only — comments always server)
      ==================================================== */

      let post =
        [...globalLivePosts, ...feedPosts, ...interactionPosts].find(
          (p) => String(p.postId) === String(postId)
        );

      /* ====================================================
         2. FETCH POST + COMMENTS FROM SERVER
      ==================================================== */

      const result = await fetchPostWithComments(postId, {
        limit: 20,
        skip: 0,
      });

      if (!mounted) return;

      if (!post) {
        post = result?.post || null;
      }

      const comments = result?.comments || [];

      /* ====================================================
         4. SEED PAGINATION STATE
      ==================================================== */

      const fetchedCount =
        comments.length;

      setCommentSkip((prev) => ({
        ...prev,
        [postId]:
          fetchedCount,
      }));

      setCommentHasMore((prev) => ({
        ...prev,
        [postId]:
          (
            post?.commentsCount || 0
          ) > fetchedCount,
      }));

      if (!mounted || !post) {
        return;
      }

      /* ====================================================
         5. HYDRATE FEEDS
      ==================================================== */

      updateAllFeeds((posts) =>
        posts.map((p) =>
          String(p.postId) ===
          String(post.postId)
            ? {
                ...p,
                ...post,
              }
            : p
        )
      );

      /* ====================================================
         6. HYDRATE COMMENTS
      ==================================================== */

      setCommentsByPost((prev) => {

        const existing =
          prev[postId] || [];

        const merged =
          new Map();

        [
          ...comments,
          ...existing,
        ].forEach((c) => {

          merged.set(
            String(c.commentId),
            c
          );

        });

        return {
          ...prev,

          [postId]:
            Array.from(
              merged.values()
            ).sort(
              (a, b) =>
                new Date(
                  a.createdAt
                ).getTime() -
                new Date(
                  b.createdAt
                ).getTime()
            ),
        };

      });

  

    } catch (err) {


    }

  })();

  return () => {
    mounted = false;
  };

}, [
  isPostPage,
  feedBootstrapDone,
  location.state?.post?.postId,
  realMyChatId,
]);

  /* Derive interactionActivity + interactionCounts from feedPosts */
  useEffect(() => {
    if (!feedPosts.length) return;
    setInteractionActivity((prev) => {
      const u = { ...prev };
      feedPosts.forEach((p) => {
        if (p.hasCommented && p.lastCommentAt && !u[p.postId])
          u[p.postId] = new Date(p.lastCommentAt).getTime();
      });
      return u;
    });
    setInteractionCounts((prev) => {
      const u = { ...prev };
      feedPosts.forEach((p) => {
        if (typeof p.unreadCount === "number" && u[p.postId] === undefined)
          u[p.postId] = p.unreadCount;
      });
      return u;
    });
  }, [feedPosts]);


const getOldestCursor = (posts, field = "createdAt") => {
  if (!posts.length) return null;
  // posts are newest-first; last item is the oldest
  const oldest = posts[posts.length - 1];
  const val    = oldest[field];
  return val ? new Date(val).toISOString() : null;
};
 
/* ─────────────────────────────────────────────
   📰 LOAD MORE — FOLLOWING FEED
───────────────────────────────────────────── */
const loadMoreFeed = async () => {
  if (feedLoading || !feedHasMore) return;
 
  try {
    setFeedLoading(true);
 
    // Only consider real following-feed posts (exclude my-post injections)
    const followingOnly = feedPosts.filter(
      (p) => String(p.author?.chatId) !== String(realMyChatId)
    );
 
    const cursor = getOldestCursor(followingOnly, "createdAt");
 
    const result = await fetchFeedHistory({ limit: 50, cursor });
    const incoming = result?.posts || [];
 
    if (!incoming.length) {
      setFeedHasMore(false);
      return;
    }
 
 
    /* ── Fetch comments per post ── */
    const commentsMap = {};
    await Promise.all(
      incoming.map(async (post) => {
        try {
          const r = await fetchPostWithComments(post.postId, { limit: 20, skip: 0 });
          commentsMap[post.postId] = r?.comments || [];
          setCommentSkip((prev)    => ({ ...prev, [post.postId]: r?.nextSkip || (r?.comments || []).length }));
          setCommentHasMore((prev) => ({ ...prev, [post.postId]: Boolean(r?.hasMore) }));
        } catch (err) {
      
        }
      })
    );
 
    /* ── Merge posts (dedupe) ── */
    setFeedPosts((prev) => {
      const map = new Map();
      [...prev, ...incoming].forEach((p) => map.set(String(p.postId), p));
      return Array.from(map.values());
    });
 
    /* ── Merge comments ── */
    setCommentsByPost((prev) => {
      const updated = { ...prev };
      Object.entries(commentsMap).forEach(([postId, comments]) => {
        const merged = new Map();
        [...(updated[postId] || []), ...comments].forEach((c) => merged.set(String(c.commentId), c));
        updated[postId] = Array.from(merged.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      });
      return updated;
    });
 
    setFeedHasMore(Boolean(result?.hasMore));
 
  } catch (err) {
  
  } finally {
    setFeedLoading(false);
  }
};
 
/* ─────────────────────────────────────────────
   🌍 LOAD MORE — GLOBAL FEED
───────────────────────────────────────────── */
const loadMoreGlobal = async () => {
  if (globalLoading || !globalHasMore) return;
 
  try {
    setGlobalLoading(true);
 
    const globalOnly = globalLivePosts.filter(
      (p) => String(p.author?.chatId) !== String(realMyChatId)
    );
 
    const cursor = getOldestCursor(globalOnly, "createdAt");
 
    const result   = await fetchGlobalFeedHistory({ limit: 50, cursor });
    const incoming = result?.posts || [];
 
    if (!incoming.length) {
      setGlobalHasMore(false);
      return;
    }
 
 
    /* ── Fetch comments per post ── */
    const commentsMap = {};
    await Promise.all(
      incoming.map(async (post) => {
        try {
          const r = await fetchPostWithComments(post.postId, { limit: 20, skip: 0 });
          commentsMap[post.postId] = r?.comments || [];
          setCommentSkip((prev)    => ({ ...prev, [post.postId]: r?.nextSkip || (r?.comments || []).length }));
          setCommentHasMore((prev) => ({ ...prev, [post.postId]: Boolean(r?.hasMore) }));
        } catch (err) {
     
        }
      })
    );
 
    /* ── Merge posts ── */
    setGlobalLivePosts((prev) => {
      const map = new Map();
      [...prev, ...incoming].forEach((p) => map.set(String(p.postId), p));
      return Array.from(map.values());
    });
 
    /* ── Merge comments ── */
    setCommentsByPost((prev) => {
      const updated = { ...prev };
      Object.entries(commentsMap).forEach(([postId, comments]) => {
        const merged = new Map();
        [...(updated[postId] || []), ...comments].forEach((c) => merged.set(String(c.commentId), c));
        updated[postId] = Array.from(merged.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      });
      return updated;
    });
 
    setGlobalHasMore(Boolean(result?.hasMore));
 
  } catch (err) {
   
  } finally {
    setGlobalLoading(false);
  }
};
 
/* ─────────────────────────────────────────────
   🔔 LOAD MORE — INTERACTIONS
   Interactions are sorted by updatedAt, so the
   cursor field is "updatedAt".
───────────────────────────────────────────── */
const loadMoreInteractions = async () => {
  if (interactionLoading || !interactionHasMore) return;
 
  try {
    setInteractionLoading(true);
 
    const cursor = getOldestCursor(interactionPosts, "updatedAt");
 
    const result   = await fetchInteractionHistory({ limit: 50, cursor });
    const incoming = result?.posts || [];
 
    if (!incoming.length) {
      setInteractionHasMore(false);
      return;
    }

 
    /* ── Fetch comments per post ── */
    const commentsMap = {};
    await Promise.all(
      incoming.map(async (post) => {
        try {
          const r = await fetchPostWithComments(post.postId, { limit: 20, skip: 0 });
          commentsMap[post.postId] = r?.comments || [];
          setCommentSkip((prev)    => ({ ...prev, [post.postId]: r?.nextSkip || (r?.comments || []).length }));
          setCommentHasMore((prev) => ({ ...prev, [post.postId]: Boolean(r?.hasMore) }));
        } catch (err) {
     
        }
      })
    );
 
    /* ── Merge posts ── */
    setInteractionPosts((prev) => {
      const map = new Map();
      [...prev, ...incoming].forEach((p) => map.set(String(p.postId), p));
      return Array.from(map.values());
    });
 
    /* ── Merge comments ── */
    setCommentsByPost((prev) => {
      const updated = { ...prev };
      Object.entries(commentsMap).forEach(([postId, comments]) => {
        const merged = new Map();
        [...(updated[postId] || []), ...comments].forEach((c) => merged.set(String(c.commentId), c));
        updated[postId] = Array.from(merged.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      });
      return updated;
    });
 
    setInteractionHasMore(Boolean(result?.hasMore));
 
  } catch (err) {
 
  } finally {
    setInteractionLoading(false);
  }
};
 

const loadMoreMyPosts = async () => {
  if (myPostsLoading || !myPostsHasMore) return;
 
  try {
    setMyPostsLoading(true);
 
    // Cursor = createdAt of the oldest MY post currently on screen
    const myPosts = feedPosts.filter(
      (p) => String(p.author?.chatId) === String(realMyChatId)
    );
 
    const oldest    = myPosts.length ? myPosts[myPosts.length - 1] : null;
    const cursor    = oldest?.createdAt
      ? new Date(oldest.createdAt).toISOString()
      : null;
 
    const result   = await fetchMyPostHistory({ limit: 50, cursor });
    const incoming = result?.posts || [];
 
    if (!incoming.length) {
      setMyPostsHasMore(false);
      return;
    }
 
    /* ── Fetch comments per post ── */
    const commentsMap = {};
    await Promise.all(
      incoming.map(async (post) => {
        try {
          const r = await fetchPostWithComments(post.postId, { limit: 20, skip: 0 });
          commentsMap[post.postId] = r?.comments || [];
          setCommentSkip((prev)    => ({ ...prev, [post.postId]: r?.nextSkip || (r?.comments || []).length }));
          setCommentHasMore((prev) => ({ ...prev, [post.postId]: Boolean(r?.hasMore) }));
        } catch (err) {
      
        }
      })
    );
 
    /* ── Merge posts (dedupe) ── */
    setFeedPosts((prev) => {
      const map = new Map();
      [...prev, ...incoming].forEach((p) => map.set(String(p.postId), p));
      return Array.from(map.values());
    });
 
    /* ── Merge comments ── */
    setCommentsByPost((prev) => {
      const updated = { ...prev };
      Object.entries(commentsMap).forEach(([postId, comments]) => {
        const merged = new Map();
        [...(updated[postId] || []), ...comments].forEach((c) =>
          merged.set(String(c.commentId), c)
        );
        updated[postId] = Array.from(merged.values()).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      });
      return updated;
    });
 
    setMyPostsHasMore(Boolean(result?.hasMore));
 
  } catch (err) {
   
  } finally {
    setMyPostsLoading(false);
  }
};


const loadMoreComments = async (postId) => {

  if (!postId) return;

  if (commentLoading[postId]) return;

  if (commentHasMore[postId] === false) return;

  try {

    setCommentLoading((prev) => ({
      ...prev,
      [postId]: true,
    }));

    const result =
      await fetchPostWithComments(
        postId,
        {
          limit: 100,
          skip: commentSkip[postId] || 0,
        }
      );

    const incoming =
      result?.comments || [];

    setCommentsByPost((prev) => {

      const existing =
        prev[postId] || [];

      const map = new Map();

      [...existing, ...incoming]
        .forEach((comment) => {

          map.set(
            String(comment.commentId),
            comment
          );

        });

      return {
        ...prev,
        [postId]:
          Array.from(map.values()),
      };

    });

    setCommentSkip((prev) => ({
      ...prev,
      [postId]:
        result?.nextSkip ||
        (
          (commentSkip[postId] || 0) +
          incoming.length
        ),
    }));

    setCommentHasMore((prev) => ({
      ...prev,
      [postId]:
        Boolean(result?.hasMore),
    }));

  } catch (err) {


  } finally {

    setCommentLoading((prev) => ({
      ...prev,
      [postId]: false,
    }));

  }
};

  /* ═══════════════════════════════════════
     DERIVED VALUES
  ═══════════════════════════════════════ */
    const myPosts = useMemo(() => {
    if (!realMyChatId) return [];
    const map = new Map();
    [...globalLivePosts, ...feedPosts].forEach((p) => map.set(p.postId, p));
    return [...map.values()].filter((p) => String(p.author?.chatId) === String(realMyChatId));
  }, [globalLivePosts, feedPosts, realMyChatId]);

  /* 👤 Posts by whichever author was tapped via UserInfoOverlay's Posts button */
  const viewedAuthorPosts = useMemo(() => {
    if (!viewedPostsUser) return [];
    const map = new Map();
    [...globalLivePosts, ...feedPosts].forEach((p) => map.set(p.postId, p));
    return [...map.values()].filter(
      (p) => String(p.author?.chatId) === String(viewedPostsUser.chatId)
    );
  }, [globalLivePosts, feedPosts, viewedPostsUser]);

 /* Opens an author's posts inside UserInfoOverlay (embedded — does NOT
   navigate the page). See exitAuthorPosts below for the separate
   full-page "My Posts" tab flow, which is unaffected by this. */
const openAuthorPosts = (u) => {
  if (!u) return;

  const chatId = String(u.chatId);

  /* ---------------------------------------------
     MY OWN POSTS
     Already prefetched by fetchMyMediaPosts()
  --------------------------------------------- */
  if (chatId === String(realMyChatId)) {
    setViewedAuthorMediaPosts(myMediaPosts);
    setViewedPostsUser(null);
    return;
  }

  /* ---------------------------------------------
     OTHER USER'S POSTS
     Read directly from the author prefetch cache.
     NO API REQUEST HERE.
  --------------------------------------------- */
  const cachedAuthorMedia =
    authorMediaCacheRef.current.get(chatId);

  setViewedAuthorMediaPosts(
    Array.isArray(cachedAuthorMedia?.posts)
      ? cachedAuthorMedia.posts
      : []
  );

  setViewedPostsUser(u);
};

/* Called when the user backs out of the embedded posts view inside
   UserInfoOverlay. Only clears the viewed author — it must NOT touch
   activeTab, since the overlay can be opened from any tab. */
const closeAuthorPosts = () => {
  setViewedPostsUser(null);
};

/* Full-page "My Posts" / author-posts tab flow (nav bar, desktop right
   column, etc.) — kept as-is for that separate entry point. */
const exitAuthorPosts = () => {
  setViewedPostsUser(null);
  setActiveTab(TABS.FEED);
};

/* Opens a group from UserInfoOverlay (desktop + mobile) */
const handleOpenGroupFromUserInfo = (group) => {
  if (!group) return;

  const fullGroup =
    groups.find(
      (g) => String(g.groupId) === String(group.groupId)
    ) || {
      ...group,
      type: "group",
    };

  setActiveTab(TABS.CHATS);

  setSelectedItem(null);

  setTimeout(() => {
    setSelectedItem({
      ...fullGroup,
      type: "group",
    });

    socket?.emit("join-group", {
      groupId: fullGroup.groupId,
      chatId: realMyChatId,
    });
  }, 0);
};


const authorPostsAvatarUrl =
  currentUser?.profileImageUrl || currentUser?.avatarUrl || null;

/* Builds the actual MyPost element for embedding inside UserInfoOverlay,
   reusing the same feed handlers/data already wired up here — so the
   overlay doesn't need a dozen props threaded through it. */
const renderAuthorPosts = () => (
  <MyPost
    myChatId={realMyChatId}
    users={users}
    myPosts={viewedPostsUser ? viewedAuthorPosts : myPosts}
    viewedUser={viewedPostsUser}
    avatarUrl={authorPostsAvatarUrl}
    myDisplayName={myDisplayName}
    onLikePost={feedSocket.likePost}
    onSupportPost={feedSocket.supportPost}
    onRepost={feedSocket.repostPost}
    onDeletePost={feedActionSocket.deletePost}
    onEditPost={feedActionSocket.editPost}
    feedSocket={feedSocket}
    onLoadMore={undefined}
    hasMore={false}
    loading={false}
    embedded
  />
);
  const commentedPosts = useMemo(() => {
    if (!realMyChatId) return [];
    return [...interactionPosts].sort((a, b) => {
      const aT = Number(interactionActivity[a.postId] || a.latestCommentPreview?.time || a.lastCommentAt) || 0;
      const bT = Number(interactionActivity[b.postId] || b.latestCommentPreview?.time || b.lastCommentAt) || 0;
      return bT - aT;
    });
  }, [interactionPosts, interactionActivity, realMyChatId]);

  const interactionsBadgeCount = useMemo(
    () => Object.values(interactionCounts || {}).reduce((s, v) => s + v, 0),
    [interactionCounts]
  );

  const notificationsBadgeCount = useMemo(
  () =>
    Array.isArray(notificationCounts)
      ? notificationCounts.reduce((s, n) => {
          if (
            n.type === "post_support" ||
            n.type === "comment_support"
          ) {
            return s + (n.isRead ? 0 : 1);
          }
          return s + (n.unreadCount || 0);
        }, 0)
      : 0,
  [notificationCounts]
);
  /* ═══════════════════════════════════════
     SIMPLE HELPERS
  ═══════════════════════════════════════ */
 const toggleTheme = () => {
  const next = theme === "light" ? "dark" : "light";

  setTheme(next);

  if (next === "dark") {
    // User explicitly selected dark mode.
    localStorage.setItem("theme", "dark");
    localStorage.setItem("themeSource", "manual");
  } else {
    // User switched back to light mode.
    // Remove the saved dark-mode preference so the app
    // no longer keeps a dark theme in localStorage.
    localStorage.removeItem("theme");
    localStorage.removeItem("themeSource");
  }
};

  const setDefaultTab = (tab) => { localStorage.setItem("defaultTab", tab); setActiveTab(tab); };
  const openConnect   = () => { setActiveTab(TABS.CONNECT); userSocket.getNearbyUsers(); };

  const uploadFeedMedia = async (files, onProgress) => {
    const uploads = [];
    for (const file of files) {
      const fileKey = `${file.name}-${file.size}`;
      try {
        const result = await uploadToR2(file, {
          type: "post",
          onProgress: (pct) => onProgress?.(fileKey, pct),
        });
        const url = result?.url;
        if (!url) continue;
        uploads.push({ type: file.type.startsWith("video") ? "video" : "image", url, mimeType: file.type, size: file.size, fileKey });
      } catch (err) {
        // Skip this file on failure, continue uploading the rest
      }
    }
    return uploads;
  };

  /* ═══════════════════════════════════════
     RENDER GUARDS
  ═══════════════════════════════════════ */
  if (!authChecked) return null;

  if (!currentUser) {
    if (authScreen === "account") return <AccountView onSignIn={() => setAuthScreen("login")} />;
    if (authScreen === "forgot")  return <ForgotPasswordView onBack={() => setAuthScreen("login")} />;
    return <LoginView onLoginSuccess={handleLoginSuccess} />;
  }

     const isSocialView =
  activeTab === TABS.FEED || activeTab === TABS.MY_POSTS ||
  activeTab === TABS.INTERACTIONS || activeTab === TABS.NOTIFICATIONS ||
  activeTab === TABS.MOMENT ||
   activeTab === TABS.MY_MEDIA_POSTS;   // ← ADD (line 4529)

  /* Buy Data page, built once here and passed to both layouts */
  const buyDataPage = (
    <BuyData
      userId={currentUser?._id}
      data={buyDataStore}
      ghsxWallet={ghsxWallet}
      uswdWallet={uswdWallet}
      onSuccess={refreshAfterUswdPurchase}
      onBack={() => setActiveTab(TABS.CHATS)}
    />
  );

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
  <AuthorPostsProvider
    value={{ openAuthorPosts, closeAuthorPosts, renderAuthorPosts }}
  >
      <div
  className={`relative flex w-full ${
    mobileView && activeTab === TABS.FEED && !isPostPage
      ? "min-h-[100dvh] overflow-visible"
      : "h-[100dvh] overflow-hidden"
  } ${
    theme === "dark"
      ? "dark bg-neutral-800 text-neutral-200"
      : "bg-neutral-50 text-neutral-900"
  }`}
>
      <ToastContainer />

      {isAppInvitePage ? (
        <AppInvite currentUser={currentUser} onContinue={() => navigate("/", { replace: true })} />

   ) : isPostPage ? (
  <>
    <Post
      myChatId={realMyChatId}
      myUserName={myUserName}
      myDisplayName={myDisplayName}
      users={users}
      feedPosts={[...globalLivePosts, ...feedPosts]}
      comments={commentsByPost[location.state?.post?.postId] || []}
      avatarUrl={currentUser?.profileImageUrl || currentUser?.avatarUrl || null}
      onLikePost={feedSocket.likePost}
      onLikeComment={feedSocket.likeComment}
      onRepostComment={feedSocket.repostComment}
      onViewComment={feedSocket.viewPost}
      onAddComment={feedSocket.addComment}
      onRepost={feedSocket.repostPost}
      feedActionSocket={feedActionSocket}
      feedSocket={feedSocket}
        mediaSocket={mediaSocket}
      onLoadMoreComments={loadMoreComments}
      commentHasMore={commentHasMore[location.state?.post?.postId] ?? false}
      commentLoading={commentLoading[location.state?.post?.postId] ?? false}
      uploadFeedMedia={uploadFeedMedia}
    />

    <MobileChatPanel
      mobileView={mobileView}
      activeTab={activeTab}
      TABS={TABS}
      selectedItem={selectedItem}
      setSelectedItem={setSelectedItem}
      inviteToken={inviteToken}
      socket={socket}
      myChatId={realMyChatId}
      myUserName={myUserName}
      handleInviteSuccess={handleInviteSuccess}
      handleInviteFailure={handleInviteFailure}
      interactionsBadgeCount={interactionsBadgeCount}
      notificationsBadgeCount={notificationsBadgeCount}
        feedBadgeCount={unreadFeedCount}
      messages={messages}
      setMessages={setMessages}
      typingUsers={typingUsers}
      groupTyping={groupTyping}
      groupMembers={groupMembers}
      groups={groups}
      setGroupMembers={setGroupMembers}
      setGroups={setGroups}
      showToast={showToast}
      users={users}
      setUsers={setUsers}
      currentUser={currentUser}
      input={input}
      setInput={setInput}
      selectedReply={selectedReply}
      setSelectedReply={setSelectedReply}
      typingTimeoutRef={typingTimeoutRef}
       chatInstances={chatInstances}
      activeChatKey={activeChatKey}
      getUserKey={getUserKey}
      getGroupKey={getGroupKey}
      pendingMedia={pendingMedia}
      setPendingMedia={setPendingMedia}
      isUploading={isUploading}
      setIsUploading={setIsUploading}
      uploadProgress={uploadProgress}
      setUploadProgress={setUploadProgress}
      p2pOrders={p2pOrders}
      orderTimers={orderTimers}
      activeP2POrder={activeP2POrder}
      feedSocket={feedSocket}
      scheduledMessages={scheduledMessages}
      onConfirmPayment={handleConfirmPayment}
      avatarZoom={avatarZoom}
      setAvatarZoom={setAvatarZoom}
      isPostPage={true}
      MOBILE_PRIMARY_TABS={MOBILE_PRIMARY_TABS}
      isSocialView={isSocialView}
      setActiveTab={setActiveTab}
      openConnect={openConnect}
      cryptoFns={cryptoFns}
    />
  </>
) : (
        <>
          <DesktopSocialLayout
            mobileView={mobileView}
            isSocialView={isSocialView}
            isPostPage={isPostPage}
            currentUser={currentUser}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleLogout={handleLogout}
            TABS={TABS}
            myChatId={realMyChatId}
            users={users}
               groups={groups}
            groupMembers={groupMembers}
            setSelectedItem={setSelectedItem}
            myPosts={myPosts}
            commentedPosts={commentedPosts}
            globalLivePosts={globalLivePosts}
            followingPosts={feedPosts}
            myUserName={myUserName}
              myDisplayName={myDisplayName}
              uswdWallet={uswdWallet}
            interactionsBadgeCount={interactionsBadgeCount}
            notificationsBadgeCount={notificationsBadgeCount}
            notificationCounts={notificationCounts}
            feedPosts={feedPosts}
            feedSocket={feedSocket}
          /* Moment (new) */
globalMediaLivePosts={globalMediaLivePosts}
followingMediaPosts={followingMediaPosts}
commentedMediaPosts={commentedMediaPosts}
myMediaPosts={myMediaPosts}
mediaNotifications={mediaNotifications}
mediaCommentsByPost={mediaCommentsByPost}
mediaSocket={mediaSocket}
            uploadFeedMedia={uploadFeedMedia}
            feedActionSocket={feedActionSocket}
            onViewPost={feedSocket.viewPost}
            setDefaultTab={setDefaultTab}
            onLoadMoreFeed={loadMoreFeed}
            viewedPostsUser={viewedPostsUser}
  viewedAuthorPosts={viewedAuthorPosts}
  onExitAuthorPosts={exitAuthorPosts}
    handleOpenGroupFromUserInfo={handleOpenGroupFromUserInfo}
  onLoadMoreGlobal={loadMoreGlobal}
  onLoadMoreInteractions={loadMoreInteractions}
  feedHasMore={feedHasMore}
  feedLoading={feedLoading}
  globalHasMore={globalHasMore}
  globalLoading={globalLoading}
  interactionHasMore={interactionHasMore}
  interactionLoading={interactionLoading}
   myPostsHasMore={myPostsHasMore}
  myPostsLoading={myPostsLoading}
  onLoadMoreMyPosts={loadMoreMyPosts}
          />

          <DesktopChatLayout
            mobileView={mobileView}
            isSocialView={isSocialView}
            isPostPage={isPostPage}
            users={users}
            groups={groups}
            setGroups={setGroups}
            groupMembers={groupMembers}
            setGroupMembers={setGroupMembers}
            setUsers={setUsers}
            showToast={showToast}
            typingUsers={typingUsers}
            groupTyping={groupTyping}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedItem={selectedItem}
              chatInstances={chatInstances}
  activeChatKey={activeChatKey}
            setSelectedItem={setSelectedItem}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            createGroupMode={createGroupMode}
            setCreateGroupMode={setCreateGroupMode}
            menuRef={menuRef}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            openConnect={openConnect}
            handleLogout={handleLogout}
            currentUser={currentUser}
            setAvatarZoom={setAvatarZoom}
            inviteToken={inviteToken}
            socket={socket}
            myChatId={realMyChatId}
            myUserName={myUserName}
            myDisplayName={myDisplayName}
            handleInviteSuccess={handleInviteSuccess}
            handleInviteFailure={handleInviteFailure}
            TABS={TABS}
            p2pOffers={p2pOffers}
            p2pEvent={p2pEvent}
            setP2pOffers={setP2pOffers}
            setMyAds={setMyAds} 
            ghsxSocket={ghsxSocket}
            myUsdtAd={myUsdtAd}
            fallbackItem={fallbackItem}
             viewedPostsUser={viewedPostsUser}
  viewedAuthorPosts={viewedAuthorPosts}
  onExitAuthorPosts={exitAuthorPosts}
setMyUsdtAd={setMyUsdtAd}
 myAds={myAds} 
            p2pOrders={p2pOrders}
              clearP2pEvent={() => setP2pEvent(null)}
            orderTimers={orderTimers}
             onConfirmPayment={
    handleConfirmPayment
  }
  uswdPricing={uswdPricing}
ghsxPricing={ghsxPricing}
          wallet={wallet}
           ghsxHistory={ghsxHistory}
            setActiveP2POrder={setActiveP2POrder}
            activeP2PPage={activeP2PPage}
            setActiveP2PPage={setActiveP2PPage}
            setMessages={setMessages}
            getUserKey={getUserKey}
            uuid={uuid}
         
onCreateUsdtAd={(payload) =>
  usdt.createUsdtAd({
    ...payload,
    userId: currentUser?._id,
  })
}

onGetMyUsdtAd={() =>
  usdt.getMyUsdtAd()
}
onUpdateUsdtAd={(payload) =>
  usdt.updateUsdtAd(payload)
}

onCancelUsdtAd={(payload) =>
  usdt.cancelUsdtAd(payload)
}

onCreateUsdtOrder={(payload) =>
  usdt.createUsdtOrder(payload)
}
sellerPaymentDetails={
   sellerPaymentDetails
}

sellerCurrency={
   sellerCurrency
}

loadSellerPaymentDetails={
   loadSellerPaymentDetails
}
refreshOffers={
   refreshOffers
}
            assetView={assetView}
            setAssetView={setAssetView}
            sendAsset={sendAsset}
            setSendAsset={setSendAsset}
             uswdWallet={uswdWallet}
             ghsxWallet={ghsxWallet}
  uswdLoading={uswdWalletLoading}
  uswdError={uswdWalletError}
  isApprovedSeller={isApprovedSeller}
  onRefreshUswdBalance={refreshUswdBalance}
  onUswdPurchaseSuccess={refreshAfterUswdPurchase}
  buyDataPage={buyDataPage}
     usdtSendSuccessRef={usdtSendSuccessRef}
    usdtSendErrorRef={usdtSendErrorRef}
  uswdSocket={uswdSocket}
  uswdHistory={uswdHistory}
  usdtHistory={usdtHistory}
            usdt={usdt}
               usdtSocket={usdt}
            usdtAddress={usdtAddress}
            usdtBalance={usdtBalance}
            nearbyUsers={nearbyUsers}
            searchUsers={searchUsers}
chatKey={activeChatKey}
            feedSocket={feedSocket}
            mediaSocket={mediaSocket}
            uploadToR2={uploadToR2}
            settingsSocket={settingsSocket}
            theme={theme}
            toggleTheme={toggleTheme}
            messages={messages}
            typingTimeoutRef={typingTimeoutRef}
            getGroupKey={getGroupKey}
            input={input}
            setInput={setInput}
            selectedReply={selectedReply}
            setSelectedReply={setSelectedReply}
           
            pendingMedia={pendingMedia}
            setPendingMedia={setPendingMedia}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
           activeP2POrder={activeP2POrder}
           statusSocket={statusSocket}
statusNotifications={statusNotifications}
setStatusNotifications={setStatusNotifications}
myStatusIds={myStatusIds}
hasUnreadStatus={hasUnreadStatus}
            scheduledMessages={scheduledMessages}
            setDefaultTab={setDefaultTab}
            rentRefundRequest={rentRefundRequest}
setRentRefundRequest={setRentRefundRequest}
 notificationsBadgeCount={notificationsBadgeCount}
            cryptoFns={cryptoFns}
          />

          {createGroupMode && (
            <GroupOverlay
              users={users}
              selectedUsersForGroup={selectedUsersForGroup}
              setSelectedUsersForGroup={setSelectedUsersForGroup}
              setCreateGroupMode={setCreateGroupMode}
              socket={socket}
              currentUser={currentUser}
            />
          )}

          <MobileAppLayout
            mobileView={mobileView}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            TABS={TABS}
            isPostPage={isPostPage}
            socket={socket}
            myChatId={realMyChatId}
            myUserName={myUserName}
              myDisplayName={myDisplayName}
                       currentUser={currentUser}
            users={users}
            groups={groups}
            groupMembers={groupMembers}
            typingUsers={typingUsers}
            groupTyping={groupTyping}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            createGroupMode={createGroupMode}
            setCreateGroupMode={setCreateGroupMode}
            menuRef={menuRef}
            openConnect={openConnect}
            handleLogout={handleLogout}
            setAvatarZoom={setAvatarZoom}
            globalLivePosts={globalLivePosts}
            followingPosts={feedPosts}
            commentedPosts={commentedPosts}
            myPosts={myPosts}
             viewedPostsUser={viewedPostsUser}
            viewedAuthorPosts={viewedAuthorPosts}
            onExitAuthorPosts={exitAuthorPosts}
            feedSocket={feedSocket}
          /* Moment (new) */
globalMediaLivePosts={globalMediaLivePosts}
followingMediaPosts={followingMediaPosts}
commentedMediaPosts={commentedMediaPosts}
myMediaPosts={myMediaPosts}
mediaNotifications={mediaNotifications}
mediaCommentsByPost={mediaCommentsByPost}
mediaSocket={mediaSocket}
            feedActionSocket={feedActionSocket}
            uploadFeedMedia={uploadFeedMedia}
            interactionsBadgeCount={interactionsBadgeCount}
            notificationsBadgeCount={notificationsBadgeCount}
            notificationCounts={notificationCounts}
            feedPosts={feedPosts}
            commentsByPost={commentsByPost}
            p2pOffers={p2pOffers}
            setMyAds={setMyAds} 
            myUsdtAd={myUsdtAd}
setMyUsdtAd={setMyUsdtAd}
 myAds={myAds} 
              clearP2pEvent={() => setP2pEvent(null)}
            setP2pOffers={setP2pOffers}
            p2pOrders={p2pOrders}
            p2pEvent={p2pEvent}
            orderTimers={orderTimers}
          uswdPricing={uswdPricing}
ghsxPricing={ghsxPricing}  
          wallet={wallet}
          usdtHistory={usdtHistory}
            activeP2POrder={activeP2POrder}
            setActiveP2POrder={setActiveP2POrder}
            activeP2PPage={activeP2PPage}
            setActiveP2PPage={setActiveP2PPage}
            setMessages={setMessages}
            getUserKey={getUserKey}
            ghsxSocket={ghsxSocket}
            uuid={uuid}
          
onCreateUsdtAd={(payload) =>
  usdt.createUsdtAd({
    ...payload,
    userId: currentUser?._id,
  })
}

onGetMyUsdtAd={() =>
  usdt.getMyUsdtAd()
}
onUpdateUsdtAd={(payload) =>
  usdt.updateUsdtAd(payload)
}

onCancelUsdtAd={(payload) =>
  usdt.cancelUsdtAd(payload)
}

onCreateUsdtOrder={(payload) =>
  usdt.createUsdtOrder(payload)
}
sellerPaymentDetails={
   sellerPaymentDetails
}

sellerCurrency={
   sellerCurrency
}

loadSellerPaymentDetails={
   loadSellerPaymentDetails
}

refreshOffers={
   refreshOffers
}
            openDmChat={openDmChat}
            assetView={assetView}
            setAssetView={setAssetView}
            sendAsset={sendAsset}
             uswdWallet={uswdWallet}
             ghsxWallet={ghsxWallet}
  uswdLoading={uswdWalletLoading}
  uswdError={uswdWalletError}
  isApprovedSeller={isApprovedSeller}
  onRefreshUswdBalance={refreshUswdBalance}
  onUswdPurchaseSuccess={refreshAfterUswdPurchase}
  buyDataPage={buyDataPage}
     usdtSendSuccessRef={usdtSendSuccessRef}
    usdtSendErrorRef={usdtSendErrorRef}
            setSendAsset={setSendAsset}
           uswdSocket={uswdSocket} 
           uswdHistory={uswdHistory}
           ghsxHistory={ghsxHistory}
            usdt={usdt}
               usdtSocket={usdt}
            usdtAddress={usdtAddress}
            usdtBalance={usdtBalance}
            nearbyUsers={nearbyUsers}
            searchUsers={searchUsers}
            onViewPost={feedSocket.viewPost}
            setDefaultTab={setDefaultTab}
            uploadToR2={uploadToR2}
            settingsSocket={settingsSocket}
            theme={theme}
            toggleTheme={toggleTheme}
            onLoadMoreFeed={loadMoreFeed}
  onLoadMoreGlobal={loadMoreGlobal}
  onLoadMoreInteractions={loadMoreInteractions}
  feedHasMore={feedHasMore}
  feedLoading={feedLoading}
  globalHasMore={globalHasMore}
  globalLoading={globalLoading}
  interactionHasMore={interactionHasMore}
  interactionLoading={interactionLoading}
   myPostsHasMore={myPostsHasMore}
  myPostsLoading={myPostsLoading}
  onLoadMoreMyPosts={loadMoreMyPosts}
     statusSocket={statusSocket}
statusNotifications={statusNotifications}
setStatusNotifications={setStatusNotifications}
myStatusIds={myStatusIds}
hasUnreadStatus={hasUnreadStatus}
setSelectedReply={setSelectedReply}
selectedReply={selectedReply}
rentRefundRequest={rentRefundRequest}
setRentRefundRequest={setRentRefundRequest}

          />

          <MobileChatPanel
            mobileView={mobileView}
            activeTab={activeTab}
            TABS={TABS}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
              chatInstances={chatInstances}
  activeChatKey={activeChatKey}
            inviteToken={inviteToken}
            socket={socket}
            myChatId={realMyChatId}
            myUserName={myUserName}
            handleInviteSuccess={handleInviteSuccess}
            handleInviteFailure={handleInviteFailure}
            interactionsBadgeCount={interactionsBadgeCount}
            notificationsBadgeCount={notificationsBadgeCount}
            messages={messages}
            setMessages={setMessages}
            typingUsers={typingUsers}
            groupTyping={groupTyping}
            groupMembers={groupMembers}
            groups={groups}
            setGroupMembers={setGroupMembers}
            setGroups={setGroups}
            showToast={showToast}
            users={users}
            setUsers={setUsers}
            currentUser={currentUser}
            input={input}
            setInput={setInput}
            selectedReply={selectedReply}
            setSelectedReply={setSelectedReply}
             feedBadgeCount={unreadFeedCount}
            typingTimeoutRef={typingTimeoutRef}
            fallbackItem={fallbackItem}
            getUserKey={getUserKey}
            getGroupKey={getGroupKey}
chatKey={activeChatKey}
            pendingMedia={pendingMedia}
            setPendingMedia={setPendingMedia}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            p2pOrders={p2pOrders}
             onConfirmPayment={
    handleConfirmPayment
  }
            orderTimers={orderTimers}
            activeP2POrder={activeP2POrder}
            feedSocket={feedSocket}
            mediaSocket={mediaSocket}
            avatarZoom={avatarZoom}
            setAvatarZoom={setAvatarZoom}
            isPostPage={isPostPage}
            MOBILE_PRIMARY_TABS={MOBILE_PRIMARY_TABS}
            isSocialView={isSocialView}
            setActiveTab={setActiveTab}
            openConnect={openConnect}
            cryptoFns={cryptoFns}
         
          />
        </>
      )}
        {avatarZoom && (
  <AvatarModal
    avatarZoom={avatarZoom}
    onClose={() => setAvatarZoom(null)}
  />
)}
    </div>
    </AuthorPostsProvider>
  );
}